// FleetCanvas: the range's machines, and what the review found in them, in
// panes side by side.
//
// Each pane shows one buffer. The Fleet holds a card per machine: whose it
// is, its range day by day (marked where the review found something), then
// the sessions that most need a look, a standard met first; its quiet
// sessions fold into one line until it is opened. Grouped by work, the
// machines whose sessions fall in the same topics share a frame named for the
// topics that set them apart; grouped by team, a frame per department in the
// directory. The Findings is the review's flow as nodes: what the reviews
// wrote, what the pipeline kept, what Triage checked, tied by strands as wide
// as their share and coloured by severity, and where each finding ended up as
// a card of the sessions behind it. A buffer of the reader's own holds
// whatever is put there, at whatever scale; Tucked away holds what they want
// out of sight.
//
// Anything is dragged from one pane to another, onto a buffer's name in a
// pane's foot, or onto a workspace in the list beside, and lands the size it
// was on screen. A row dragged out of its card is a session of its own, tied
// to its row by a line, across panes too. Pointing at a session or a machine
// lights it in every pane. Shift and drag picks several things at once; Cmd
// or Ctrl and Z undoes what was moved.
//
// Every card is drawn at its own scale times its pane's zoom, its words never
// smaller than they can be read. A card is painted once into an image of its
// own, and the image drawn while the pane moves; a zoom draws the images
// scaled and repaints them as it settles, a few a frame. A pane is redrawn
// only when something in it changed, at most once a frame, and pointing and
// dragging change nothing React renders.
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import HoverTooltip from "./HoverTooltip.jsx";
import TraceCanvas from "./TraceCanvas.jsx";
import { SESSION_DRAG_TYPE } from "./WorkspaceThreads.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { MENU, MENU_ITEM, MENU_ITEM_ON } from "./ui/styles.js";
import { CANVAS_SANS as SANS, alphaMix, drawScene, overMix, readTheme, roundRect } from "./LanesCanvasPlane.jsx";
import { buildPromptRootAndFilter, dimFieldRef, rangeToTimeRange, rowsToObjects, runQuery } from "../lib/analyticsClient.js";
import { loadClusterTree } from "../lib/clusterTree.js";
import { buildLaneScene, layoutRows } from "../lib/laneScene.js";
import { bucketLanes, buildClock, cardRows, fitRowsToHeight, turnTimes } from "../lib/sessionWorkbench.js";
import { warmGrammar } from "../lib/syntax/parserHost.js";
import { unwrapCodeModeTurns } from "../lib/traceCodeModeCells.js";
import { buildTraceLanes } from "../lib/traceLanes.js";
import { readShellWork } from "../lib/traceShellWork.js";
import { query } from "../lib/dataClient.js";
import { dimensionMeta, useLabels } from "../lib/dimensionDefs.js";
import { getDotTilePattern } from "../lib/renderers.js";
import { buildReviewFlow } from "../lib/reviewFlow.js";
import { fetchReviewFlow, reviewsWindow } from "../lib/scopeReviews.js";
import { toApiRange } from "../lib/shell.js";
import { useThemeVersion } from "../lib/theme.js";
import { CLUSTER_HUES } from "../lib/traceAnatomy.js";
import {
  keepTraceCanvas, layOnTraceCanvas, releaseTraceCanvasInDeck, restoreTraceCanvasLayers, showTraceCanvasInDeck, takeOffTraceCanvas, traceCanvasOpen, traceCanvasSnapshot,
  useTraceCanvas, useTraceCanvases,
} from "../lib/traceCanvasStore.js";
import { onDeckWorkspace } from "../lib/deckRequests.js";
import {
  ASKABLE, OUTCOMES, SEV_RANK, VERDICT_SHORT, VERDICT_WORD, agentName, machineLabel, outcomesOf, personName, titleOf, useFleetModel,
} from "./fleetModel.js";

const NONE = [];
const DOT = " \u00b7 ";
const HOUR = 3_600_000;
const DAY = 24 * HOUR;
// A card in its own units (its scale `s` sizes the whole): its head, its
// strip, a row per session shown, and a foot for the rest.
const CARD_U = 340;
const OUTCOME_U = 380;
const CARD_MIN = 260;
const CARD_MAX = 720;
const HEAD_U = 52;
const OUT_HEAD_U = 58;
const STRIP_U = 30;
const ROW_U = 46;
const GROUP_ROW_U = 62;
const CLEAN_U = 30;
const FOOT_U = 30;
const SHOW = 6;
const GAP_U = 28;
const PITCH = CARD_U + GAP_U;
// A frame round a group of cards: its head, its margin, and the room between
// frames.
const FRAME_HEAD = 84;
const FRAME_PAD = 16;
const FRAME_GAP = 56;
// The findings' flow: its nodes, and the step from one column to the next.
const STAGE_W = 220;
const STAGE_H = 96;
const DROP_H = 66;
const STAGE_STEP = 330;
// A session out of its card, in its own units.
const SESSION_W = 320;
// How wide a session pulled out of its card grows to on screen.
const PULL_W = 300;
// The corner a card is sized by, and the edge its width is, on screen.
const GRIP = 14;
const EDGE = 6;
// The smallest words drawn; below it there are no words, only colour.
const MIN_PX = 9.5;
// Machines whose mixes of work are at least this alike, on average across
// the group, share a frame.
const WORK_ALIKE = 0.35;
// Panes: how many side by side, and the least width each keeps.
const MAX_PANES = 4;
const PANE_MIN = 360;
// A card's painted image: the margin its shadow needs, the device pixels all
// images may hold together and one may on its own, how long a frame may spend
// repainting what a zoom left behind, and how long a zoom lasts after its
// last step.
const SPRITE_PAD = 14;
const SPRITE_BUDGET = 24_000_000;
const SPRITE_MAX = 6_000_000;
const REPAINT_MS = 8;
const ZOOM_SETTLE_MS = 140;
const STORE_KEY = "origin-fleet-canvas-v5";
const OLD_STORE_KEYS = ["origin-fleet-canvas-v4", "origin-fleet-canvas-v3"];
const FLEET = "fleet";
const FINDINGS = "findings";
const TUCKED = "tucked";
// A saved workspace shown in a pane, as its board: its buffer is "ws:" and
// the workspace's id. What is on it is the workspace's own, laid on from the
// deck or worked on the board itself.
const WS = "ws:";
// A workspace keeps its anchor and its newest 39 sessions (traceCanvasStore).
const MAX_ON_WORKSPACE = 40;
const isWs = (buf) => typeof buf === "string" && buf.startsWith(WS);
const wsOf = (buf) => (isWs(buf) ? buf.slice(WS.length) : null);
const wsTitle = (id) => traceCanvasSnapshot(id)?.title || "Untitled investigation";
const GROUPINGS = [["focus", "Look now"], ["concern", "Machines"], ["work", "Work"], ["team", "Team"]];
const STAGE_ABOUT = {
  written: "Every finding the reviews wrote in the range, kept or not.",
  kept: "What the pipeline kept: what Triage and Investigate read.",
  withheld: "Written, then withheld by the pipeline.",
  context_only: "Kept as context for other findings, not on its own.",
  unsupported: "Dropped: the trace did not support it.",
  triaged: "Read by Triage against its own trace.",
  not_triaged: "Kept, not read by Triage yet.",
  not_loaded: "Kept, but older than the newest findings this reads.",
};
const CAPTION = ["Reviews", "Pipeline", "Triage"];

const smooth = (a, b, x) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// Words at a scale, within what can be read: no smaller than MIN_PX, and
// not much larger than their own size however far a card is grown. Sizes go
// in half pixels, so zooms near each other measure their words once.
const fs = (base, z) => Math.round(clamp(base * z, MIN_PX, base * 1.15) * 2) / 2;
const P = (f) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${f}` });
const newId = (prefix) => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const raf = (f) => (typeof requestAnimationFrame === "function" ? requestAnimationFrame(f) : setTimeout(f, 16));

// -- what is kept -------------------------------------------------------------------

// Where everything is: the buffers, the panes and what each shows (with the
// view it left each buffer at), each machine's and outcome's card (`boxes`:
// its buffer, place, scale, width, and whether it is open), each session out
// of its card (`cards`), each group's frame where it was put by hand, and
// which card was touched last.
const BUILT_INS = [{ id: FLEET, name: "Fleet" }, { id: FINDINGS, name: "Findings" }];
const freshPane = (buf = FLEET, w = 1) => ({ id: newId("p"), buf, group: "focus", views: {}, w });
const freshStore = () => ({ v: 5, lookFirst: true, buffers: [...BUILT_INS], panes: [freshPane(FLEET)], boxes: {}, cards: {}, frames: {}, z: [] });
function normalStore(s) {
  const buffers = (s.buffers || []).filter((b) => b?.id).map(({ id, name }) => ({ id, name: name || "Buffer" }));
  if (!buffers.some((b) => b.id === FLEET)) buffers.unshift({ id: FLEET, name: "Fleet" });
  if (!buffers.some((b) => b.id === FINDINGS)) buffers.splice(1, 0, { id: FINDINGS, name: "Findings" });
  const known = new Set([...buffers.map((b) => b.id), TUCKED]);
  const panes = (s.panes || []).filter((p) => p?.id).slice(0, MAX_PANES).map((p) => ({
    ...p, buf: known.has(p.buf) || isWs(p.buf) ? p.buf : FLEET, group: GROUPINGS.some(([id]) => id === p.group) ? p.group : "focus", views: p.views || {}, w: p.w > 0 ? p.w : 1,
  }));
  // The fleet a reader had not turned to a view of their own opens on Look
  // now, once: a pane turned elsewhere after stays so.
  if (!s.lookFirst) {
    for (const p of panes) {
      if (p.buf === FLEET && p.group === "concern") {
        p.group = "focus";
        p.views = { ...p.views, [FLEET]: null };
      }
    }
  }
  return {
    v: 5, lookFirst: true, buffers, panes: panes.length ? panes : [freshPane(FLEET)],
    boxes: s.boxes || {}, cards: s.cards || {}, frames: s.frames || {}, z: s.z || [],
  };
}
// An older store's one view becomes the first pane, with the views its
// buffers were left at.
function fromOlder(s) {
  const views = Object.fromEntries((s.buffers || []).filter((b) => b?.view).map((b) => [b.id, b.view]));
  return normalStore({ ...s, panes: [{ ...freshPane(s.current || FLEET), group: s.group || "concern", views }] });
}
function readStore() {
  try {
    const s = JSON.parse(globalThis.localStorage?.getItem(STORE_KEY) || "null");
    if (s?.v === 5) return { store: normalStore(s), fresh: false };
    for (const key of OLD_STORE_KEYS) {
      const old = JSON.parse(globalThis.localStorage?.getItem(key) || "null");
      if (old?.v === 3 || old?.v === 4) return { store: fromOlder(old), fresh: false };
    }
  } catch { /* a fresh deck */ }
  return { store: freshStore(), fresh: true };
}
const writeStore = (value) => {
  try { globalThis.localStorage?.setItem(STORE_KEY, JSON.stringify(value)); } catch { /* this tab only */ }
};
const cssVar = (name, fallback, host) => {
  const v = getComputedStyle(host || document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};
// Formatters made once: toLocaleString with options makes one per call, and
// the cards say a date or a count on every row they paint.
const NUM = new Intl.NumberFormat();
const WHEN = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
const DAY_AT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
const num = (n) => NUM.format(n);
const whenText = (t) => (Number.isFinite(t) ? WHEN.format(t) : "");
const dayText = (t) => (Number.isFinite(t) ? DAY_AT.format(t) : "");
const lengthText = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  if (ms >= DAY) return `${Math.floor(ms / DAY)}d ${Math.floor((ms % DAY) / HOUR)}h`;
  if (ms >= HOUR) return `${Math.floor(ms / HOUR)}h ${Math.floor((ms % HOUR) / 60_000)}m`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m`;
};
const plural = (n, one, many = `${one}s`) => `${num(n)} ${n === 1 ? one : many}`;
const pct = (share) => (share == null ? "" : share > 0 && share < 0.01 ? "<1%" : `${Math.round(share * 100)}%`);
// A title cut to a word for a line of the foot.
const short = (text, n) => {
  const t = String(text || "");
  if (t.length <= n) return t;
  const cut = t.slice(0, n - 3);
  return `${(cut.lastIndexOf(" ") > n / 2 ? cut.slice(0, cut.lastIndexOf(" ")) : cut).trimEnd()}...`;
};
// What a set of findings' verifications came to, in the Triage tab's words.
function verdictMix(findings) {
  const n = {};
  for (const f of findings) if (f.verdict) n[f.verdict] = (n[f.verdict] || 0) + 1;
  const escalated = findings.filter((f) => f.met?.length).length;
  return [
    escalated ? `${escalated} escalated` : null,
    ...Object.entries(n).sort((a, b) => b[1] - a[1]).map(([v, c]) => `${c} ${VERDICT_SHORT[v] || v}`),
  ].filter(Boolean).join(DOT);
}


// One read per key, shared by every reader of it for five minutes; a failed
// read is not kept.
const reads = new Map();
function useRead(key, read) {
  const [state, setState] = useState({ status: key ? "loading" : "idle", data: null, key });
  useEffect(() => {
    if (!key) { setState({ status: "idle", data: null, key }); return undefined; }
    let live = true;
    const hit = reads.get(key);
    const entry = hit && Date.now() - hit.at < 5 * 60_000 ? hit : { at: Date.now(), promise: read() };
    reads.set(key, entry);
    setState((cur) => (cur.key === key && cur.status === "done" ? cur : { status: "loading", data: null, key }));
    entry.promise.then(
      (data) => { if (live) setState({ status: "done", data, key }); },
      (error) => { reads.delete(key); if (live) setState({ status: "error", data: null, error, key }); },
    );
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

// Each machine's work: its prompts in the range over the topics' top level
// (the clusters' roots), and the topics' names.
async function readWork(range) {
  const timeRange = rangeToTimeRange(toApiRange(range));
  const { root, filter } = buildPromptRootAndFilter({ extraDims: ["cluster"] });
  const spec = {
    root,
    timeRange,
    output: {
      dimensions: [{ field: P("ENDPOINT_ID"), outputAlias: "ep" }, { field: dimFieldRef("cluster"), outputAlias: "cl" }],
      metrics: [{ outputAlias: "n", computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
    },
    limit: 20000,
  };
  if (filter) spec.filter = filter;
  const [rows, tree] = await Promise.all([runQuery(spec).then(rowsToObjects), loadClusterTree()]);
  const tops = new Map();
  const topOf = (id) => {
    const key = String(id);
    if (tops.has(key)) return tops.get(key);
    let cur = tree.byId.get(key);
    const seen = new Set();
    while (cur?.parentId && tree.byId.has(cur.parentId) && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = tree.byId.get(cur.parentId);
    }
    const out = cur ? cur.id : key;
    tops.set(key, out);
    return out;
  };
  const vectors = new Map();
  const names = new Map();
  for (const r of rows) {
    const ep = String(r.ep || "");
    if (!ep || r.cl == null) continue;
    const t = topOf(r.cl);
    if (!vectors.has(ep)) vectors.set(ep, new Map());
    const v = vectors.get(ep);
    v.set(t, (v.get(t) || 0) + (Number(r.n) || 0));
    if (!names.has(t)) names.set(t, String(tree.byId.get(t)?.name || "A topic").replace(/\s+/g, " ").trim());
  }
  return { vectors, names };
}

// Who each machine belongs to: the people the directory assigns it, most
// prompts first.
async function readPeople(range) {
  const timeRange = rangeToTimeRange(toApiRange(range));
  const { root, filter } = buildPromptRootAndFilter({ extraDims: ["identity"] });
  const spec = {
    root,
    timeRange,
    output: {
      dimensions: [{ field: P("ENDPOINT_ID"), outputAlias: "ep" }, { field: dimFieldRef("identity"), outputAlias: "person" }],
      metrics: [{ outputAlias: "n", computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
    },
    limit: 5000,
  };
  if (filter) spec.filter = filter;
  const rows = rowsToObjects(await runQuery(spec));
  const people = new Map();
  for (const r of rows) {
    const ep = String(r.ep || "");
    const id = String(r.person || "");
    if (!ep || !id) continue;
    if (!people.has(ep)) people.set(ep, []);
    people.get(ep).push({ id, n: Number(r.n) || 0 });
  }
  for (const list of people.values()) list.sort((a, b) => b.n - a.n);
  return { people };
}

// What the reviews wrote in the range, before the pipeline kept any of it.
function readFlowSums(range) {
  const win = reviewsWindow(toApiRange(range));
  return fetchReviewFlow({ sessionIds: null, fromMs: win.fromMs, toMs: win.toMs });
}


// -- grouping -----------------------------------------------------------------------

// Machines by the work their sessions do: each machine's prompts over the
// top-level topics, the topics most machines share weighted down, and the
// machines whose mixes are alike (on average across the group) in one frame,
// named for the topics that set it apart.
function groupByWork(machines, work) {
  const raw = work?.vectors || new Map();
  const ids = machines.map((m) => m.id).filter((id) => raw.has(id));
  const share = new Map();
  for (const id of ids) {
    const v = raw.get(id);
    const total = [...v.values()].reduce((a, x) => a + x, 0) || 1;
    share.set(id, new Map([...v].map(([t, x]) => [t, x / total])));
  }
  const df = new Map();
  for (const s of share.values()) for (const [t, x] of s) if (x >= 0.03) df.set(t, (df.get(t) || 0) + 1);
  const idf = (t) => Math.log((ids.length + 1) / ((df.get(t) || 0) + 1)) + 0.15;
  const vec = new Map();
  for (const [id, s] of share) {
    const w = new Map();
    for (const [t, x] of s) if (x >= 0.02) w.set(t, Math.sqrt(x) * idf(t));
    vec.set(id, w);
  }
  const norm = new Map([...vec].map(([id, w]) => [id, Math.sqrt([...w.values()].reduce((a, x) => a + x * x, 0)) || 1]));
  const sims = new Map();
  const alike = (a, b) => {
    const key = a < b ? `${a}|${b}` : `${b}|${a}`;
    if (!sims.has(key)) {
      let d = 0;
      for (const [t, x] of vec.get(a)) {
        const y = vec.get(b).get(t);
        if (y) d += x * y;
      }
      sims.set(key, d / (norm.get(a) * norm.get(b)));
    }
    return sims.get(key);
  };
  let groups = ids.map((id) => [id]);
  for (;;) {
    let best = null;
    for (let i = 0; i < groups.length; i += 1) {
      for (let j = i + 1; j < groups.length; j += 1) {
        let s = 0;
        for (const a of groups[i]) for (const b of groups[j]) s += alike(a, b);
        s /= groups[i].length * groups[j].length;
        if (!best || s > best.s) best = { i, j, s };
      }
    }
    if (!best || best.s < WORK_ALIKE) break;
    const merged = [...groups[best.i], ...groups[best.j]];
    groups = groups.filter((_, k) => k !== best.i && k !== best.j);
    groups.push(merged);
  }
  const used = new Set();
  const out = groups.map((members) => {
    const weight = new Map();
    const mix = new Map();
    for (const id of members) {
      for (const [t, x] of vec.get(id)) weight.set(t, (weight.get(t) || 0) + x);
      for (const [t, x] of share.get(id)) mix.set(t, (mix.get(t) || 0) + x / members.length);
    }
    const marks = [...weight].sort((a, b) => b[1] - a[1]).slice(0, 2).map(([t]) => t);
    let id = `w:${marks.join("+")}`;
    while (used.has(id)) id += "'";
    used.add(id);
    return {
      id,
      label: marks.map((t) => work.names.get(t)).join(DOT),
      members,
      topics: [...mix].sort((a, b) => b[1] - a[1]).slice(0, 4).map(([t, x]) => ({ id: t, name: work.names.get(t), share: x })),
    };
  });
  const unread = machines.filter((m) => !raw.has(m.id)).map((m) => m.id);
  if (unread.length) out.push({ id: "w:unread", label: "Not read into topics yet", members: unread, topics: [] });
  return out;
}

// Machines by team: the department the directory gives the person a machine
// belongs to, a person's machines side by side.
function groupByTeam(machines, people, rank) {
  const teams = new Map();
  for (const m of machines) {
    const who = people?.get(m.id)?.[0]?.id || null;
    const dept = who ? String(dimensionMeta("identities", who)?.department || "").trim() : "";
    const id = !who ? "t:none" : dept ? `t:${dept}` : "t:nodept";
    const label = !who ? "No one assigned in the directory" : dept || "No department in the directory";
    if (!teams.has(id)) teams.set(id, { id, label, members: [], people: new Map() });
    const team = teams.get(id);
    team.members.push(m.id);
    if (who) {
      if (!team.people.has(who)) team.people.set(who, []);
      team.people.get(who).push(m.id);
    }
  }
  return [...teams.values()].map((t) => {
    // A person's machines together, the person whose worst machine needs a
    // look most first.
    const firstOf = (list) => Math.min(...list.map((id) => rank.get(id) ?? 1e9));
    const persons = [...t.people.entries()].sort((a, b) => firstOf(a[1]) - firstOf(b[1]));
    const placed = new Set();
    const members = [];
    for (const [, list] of persons) {
      for (const id of [...list].sort((a, b) => (rank.get(a) ?? 0) - (rank.get(b) ?? 0))) {
        members.push(id);
        placed.add(id);
      }
    }
    for (const id of t.members) if (!placed.has(id)) members.push(id);
    return { id: t.id, label: t.label, members, people: persons.map(([who]) => who), topics: [] };
  });
}

// -- geometry -----------------------------------------------------------------------

// A machine's card in its own units: its rows (the flagged ones that most
// need a look, or every session once opened) and its foot for the rest.
function cardOf(m, at = {}, ready = true) {
  const wu = clamp(at.w || CARD_U, CARD_MIN, CARD_MAX);
  const open = !!at.open;
  const shown = open ? m.flagged : m.flagged.slice(0, SHOW);
  const rows = [];
  let y = HEAD_U + STRIP_U;
  for (const s of shown) { rows.push({ s, y, h: ROW_U, kind: "flag" }); y += ROW_U; }
  if (open) for (const s of m.clean) { rows.push({ s, y, h: CLEAN_U, kind: "clean" }); y += CLEAN_U; }
  const more = m.flagged.length - shown.length;
  const quiet = open ? 0 : m.clean.length;
  let foot = null;
  if (!ready) {
    foot = { y, said: "Reading what was found...", act: "" };
    y += FOOT_U;
  } else if (more || quiet) {
    foot = { y, said: more ? `${num(more)} more flagged` : `${num(quiet)} with nothing found`, act: `Show all ${num(m.sessions.length)}` };
    y += FOOT_U;
  } else if (open && (m.clean.length || m.flagged.length > SHOW)) {
    foot = { y, said: "", act: "Show what needs a look" };
    y += FOOT_U;
  }
  return { wu, hu: y + (rows.length || foot ? 4 : 0), rows, foot, open };
}
// Where Triage left findings, as a card: a row per session, worst first.
function outcomeCardOf(o, at = {}) {
  const wu = clamp(at.w || OUTCOME_U, CARD_MIN, CARD_MAX);
  const open = !!at.open;
  const shown = open ? o.groups : o.groups.slice(0, SHOW);
  const rows = [];
  let y = OUT_HEAD_U + STRIP_U;
  for (const g of shown) { rows.push({ s: g.s, y, h: GROUP_ROW_U, kind: "group", group: g }); y += GROUP_ROW_U; }
  const more = o.groups.length - shown.length;
  let foot = null;
  if (more) {
    foot = { y, said: `${num(more)} more traces`, act: `Show all ${num(o.groups.length)}` };
    y += FOOT_U;
  } else if (open && o.groups.length > SHOW) {
    foot = { y, said: "", act: "Show the first few" };
    y += FOOT_U;
  }
  return { wu, hu: y + 4, rows, foot, open };
}
// A session's card in its own units: its height for what it holds.
function sessionUnits(s) {
  const rows = s.findings.length ? s.findings.slice(0, 8).reduce((h, f) => h + (f.met?.length ? 36 : 22), 0) : 22;
  return 76 + 10 + rows + (s.findings.length > 8 ? 18 : 0) + 8 + 30;
}
// Cards in columns in the order given, each where its columns stand lowest;
// a card grown wider than a column takes as many as it needs.
function columns(items, ncols) {
  const out = new Map();
  const n = Math.max(1, ncols);
  const heights = new Array(n).fill(0);
  for (const it of items) {
    const span = clamp(Math.ceil((it.w + GAP_U) / PITCH - 0.02), 1, n);
    let best = 0;
    let bestTop = Infinity;
    for (let c = 0; c + span <= n; c += 1) {
      const top = Math.max(...heights.slice(c, c + span));
      if (top < bestTop - 1) { best = c; bestTop = top; }
    }
    out.set(it.key, { x: best * PITCH, y: bestTop });
    for (let c = best; c < best + span; c += 1) heights[c] = bestTop + it.h + GAP_U;
  }
  return out;
}
// The items packed from the top left in the order given, each where the
// skyline across the canvas is lowest, the whole about as wide as the screen
// is to its height.
function pack(items, aspect, gap = GAP_U) {
  const out = new Map();
  if (!items.length) return out;
  const area = items.reduce((a, f) => a + (f.w + gap) * (f.h + gap), 0);
  const width = Math.max(...items.map((f) => f.w), Math.sqrt(area * aspect));
  let sky = [{ x: 0, w: width, y: 0 }];
  for (const f of items) {
    let best = null;
    for (let i = 0; i < sky.length; i += 1) {
      const x = sky[i].x;
      if (x + f.w > width + 1) break;
      let y = 0;
      for (let j = i; j < sky.length && sky[j].x < x + f.w; j += 1) y = Math.max(y, sky[j].y);
      if (!best || y < best.y) best = { x, y };
    }
    if (!best) best = { x: 0, y: Math.max(...sky.map((s) => s.y)) };
    out.set(f.key, { x: best.x, y: best.y });
    const top = best.y + f.h + gap;
    const x0 = best.x;
    const x1 = best.x + f.w + gap;
    const next = [];
    for (const s of sky) {
      const s1 = s.x + s.w;
      if (s1 <= x0 || s.x >= x1) { next.push(s); continue; }
      if (s.x < x0) next.push({ x: s.x, w: x0 - s.x, y: s.y });
      if (s1 > x1) next.push({ x: x1, w: s1 - x1, y: s.y });
    }
    next.push({ x: x0, w: Math.min(x1, width) - x0, y: top });
    sky = next.filter((s) => s.w > 0).sort((a, b) => a.x - b.x);
  }
  return out;
}
const machineBox = (m, at, ready) => ({ key: `b:${m.id}`, id: m.id, kind: "machine", ep: m.id, m, s: at?.s || 1, ...cardOf(m, at || {}, ready) });
const outcomeBox = (o, at) => ({ key: `o:${o.id}`, id: `o:${o.id}`, kind: "outcome", o, s: at?.s || 1, ...outcomeCardOf(o, at || {}) });
const inFlow = (store, id) => (store.boxes[id]?.buf || FLEET) === FLEET && !Number.isFinite(store.boxes[id]?.x);

// The fleet's cards in columns, the machines that most need a look first.
function layoutFlow({ model, store, ncols }) {
  const items = model.machines.filter((m) => inFlow(store, m.id)).map((m) => {
    const b = machineBox(m, store.boxes[m.id] || {}, model.findingsReady);
    return { key: m.id, w: b.wu * b.s, h: b.hu * b.s, b };
  });
  const pos = columns(items, ncols);
  return items.map((it) => ({ ...it.b, ...pos.get(it.key), placed: false }));
}

// The fleet's cards in a frame per group, the frames across the canvas: those
// put somewhere by hand where they were put, the rest packed from the top
// left, the group that most needs a look first.
function layoutGroups({ model, store, groups, mode, aspect }) {
  const made = [];
  for (const g of groups) {
    const members = g.members.map((id) => model.byId.get(id)).filter((m) => m && inFlow(store, m.id));
    if (!members.length) continue;
    const cols = clamp(Math.ceil(Math.sqrt(members.length / 1.4)), 1, 4);
    const items = members.map((m) => {
      const b = machineBox(m, store.boxes[m.id] || {}, model.findingsReady);
      return { key: m.id, w: b.wu * b.s, h: b.hu * b.s, b };
    });
    const pos = columns(items, cols);
    let w = 0;
    let h = 0;
    for (const it of items) {
      const p = pos.get(it.key);
      w = Math.max(w, p.x + it.w);
      h = Math.max(h, p.y + it.h);
    }
    const rank = Math.min(...members.map((m) => model.rank.get(m.id) ?? 1e9));
    const flagged = members.reduce((n, m) => n + m.flagged.length, 0);
    const met = members.reduce((n, m) => n + m.met, 0);
    made.push({ g, items, pos, rank, flagged, met, w: Math.max(w, 260) + 2 * FRAME_PAD, h: FRAME_HEAD + h + FRAME_PAD });
  }
  made.sort((a, b) => a.rank - b.rank);
  const kept = store.frames?.[mode] || {};
  const packed = pack(made.filter((f) => !kept[f.g.id]).map((f) => ({ key: f.g.id, w: f.w, h: f.h })), aspect, FRAME_GAP);
  const frames = [];
  const boxes = [];
  for (const f of made) {
    const spot = kept[f.g.id] || packed.get(f.g.id) || { x: 0, y: 0 };
    frames.push({ key: `g:${f.g.id}`, kind: "frame", gid: f.g.id, g: f.g, x: spot.x, y: spot.y, w: f.w, h: f.h, placed: !!kept[f.g.id], count: f.items.length, flagged: f.flagged, met: f.met });
    for (const it of f.items) {
      const p = f.pos.get(it.key);
      boxes.push({ ...it.b, x: spot.x + FRAME_PAD + p.x, y: spot.y + FRAME_HEAD + p.y, placed: false, frame: `g:${f.g.id}` });
    }
  }
  return { frames, boxes };
}

// The flow's nodes in their columns, and the outcome cards in the last that
// are still in the Findings: those put somewhere by hand where they were put,
// the rest stacked.
function layoutFindings({ flow, outcomes, store }) {
  const stages = [];
  if (!flow) return { stages, boxes: [] };
  const last = flow.columns.length - 1;
  const offset = flow.nodes.has("written") ? 0 : 1;
  flow.columns.forEach((col, ci) => {
    if (ci === last) return;
    let y = 0;
    const ids = [...col.nodes].sort((a, b) => (flow.nodes.get(a).kind === "drop") - (flow.nodes.get(b).kind === "drop"));
    ids.forEach((id, i) => {
      const node = flow.nodes.get(id);
      const h = node.kind === "drop" ? DROP_H : STAGE_H;
      stages.push({ key: `n:${id}`, kind: "stage", id, node, caption: i === 0 ? CAPTION[ci + offset] || "" : "", x: ci * STAGE_STEP, y, w: STAGE_W, h });
      y += h + 18;
    });
  });
  const boxes = [];
  let y = 0;
  for (const o of outcomes) {
    const at = store.boxes[`o:${o.id}`] || {};
    if ((at.buf || FINDINGS) !== FINDINGS) continue;
    const b = outcomeBox(o, at);
    const placed = Number.isFinite(at.x);
    boxes.push({ ...b, x: placed ? at.x : last * STAGE_STEP + 40, y: placed ? at.y : y, placed });
    if (!placed) y += b.hu * b.s + GAP_U;
  }
  return { stages, boxes };
}

// The first page, Look now: what a person should look at, large and in the
// middle (the sessions a standard was met in: what was found and met, where
// and when, their lanes when there is room); and around it, smaller, what
// places it: to the right, the machines those ran on, their rows tied to
// them; under it, the rest of where Triage left the findings, the backlog not
// verified yet last. Only a standard met leads: with none met in the range
// the frame says so. A context card moved by hand stays where it was put; the
// sessions leading stay in their frame.
const HERO_S = 1.45;
const NEAR_S = 0.7;
const FOCUS_GAP = 120;
const HERO_MAX = 6;
function layoutFocus({ model, outcomes, store, ncols }) {
  const met = outcomes.find((o) => o.id === "met");
  const leading = met?.groups || NONE;
  const lead = leading.slice(0, HERO_MAX);
  // One column of them in a pane of ordinary width, so each reads large; two
  // or three side by side only in a wide one.
  const cols = clamp(Math.min(lead.length, Math.floor(ncols / 2.5)), 1, 3);
  const cardW = SESSION_W * HERO_S;
  const cards = [];
  const colH = new Array(cols).fill(0);
  for (const g of lead) {
    const col = colH.indexOf(Math.min(...colH));
    const hu = sessionUnits(g.s);
    cards.push({ key: `h:${g.s.id}`, sid: g.s.id, ses: g.s, from: null, x: col * (cardW + 40), y: colH[col], s: HERO_S, wu: SESSION_W, hu, hero: true });
    colH[col] += hu * HERO_S + 40;
  }
  const heroW = cols * cardW + (cols - 1) * 40;
  const heroH = Math.max(200, ...colH) - 40;
  const standards = [...new Set((met?.findings || NONE).flatMap((f) => f.met || NONE))];
  const first = leading.length > lead.length ? `the first ${lead.length} here` : null;
  // Until the findings are read, nothing can be said to have met a standard.
  const [label, said] = !model.findingsReady ? ["Reading the findings", "what met a standard leads here once they are read"]
    : lead.length ? ["A standard was met: a person should look", [`${plural(met.findings.length, "finding")} in ${plural(met.groups.length, "trace")}`, standards.length ? `met ${standards.join(", ")}` : null, first].filter(Boolean).join(DOT)]
    : ["Nothing met a standard in this range", "Triage, under it, says where every finding was left"];
  const frames = [{
    key: "g:look", kind: "frame", gid: "look", fixed: true, count: 0, tone: lead.length ? "danger" : null,
    g: { id: "look", label },
    said,
    x: -36, y: -96, w: heroW + 72, h: heroH + 132,
  }];
  const boxes = [];
  const place = (b, x, y) => {
    const at = store.boxes[b.id] || {};
    const placed = (at.buf || FLEET) === FLEET && Number.isFinite(at.x);
    boxes.push({ ...b, x: placed ? at.x : x, y: placed ? at.y : y, placed });
  };
  // Right: the machines they ran on, each its own card here.
  let ry = 0;
  for (const ep of new Set(lead.map((g) => g.s.endpointId))) {
    const m = model.byId.get(ep);
    if (!m) continue;
    const b = { ...machineBox(m, { s: NEAR_S }, model.findingsReady), key: `b:look:${m.id}`, id: `look:${m.id}` };
    place(b, heroW + FOCUS_GAP, ry);
    ry += b.hu * NEAR_S + GAP_U;
  }
  // Under it: the rest of where Triage left the findings, the backlog last.
  let ux = 0;
  const uy = heroH + FOCUS_GAP + 36;
  for (const o0 of outcomes) {
    if (o0.id === "met" && leading.length <= lead.length) continue;
    const sc = o0.id === "not_triaged" ? NEAR_S * 0.85 : NEAR_S;
    const b = outcomeBox({ ...o0, id: `look:${o0.id}`, base: o0.id }, { s: sc });
    place(b, ux, uy);
    ux += b.wu * sc + GAP_U;
  }
  return { cards, frames, boxes };
}

// What a pane draws for its buffer: frames, flow nodes and strands, cards,
// and the sessions out of their cards, in the order they were last touched.
function sceneFor({ model, store, buf, group, groups, flow, outcomes, ncols, aspect }) {
  const out = new Set(Object.keys(store.cards));
  let boxes = [];
  let frames = [];
  let stages = [];
  let strands = [];
  let leading = NONE;
  if (buf === FINDINGS) {
    ({ stages, boxes } = layoutFindings({ flow, outcomes, store }));
    if (flow) {
      const triaged = flow.nodes.get("triaged")?.count || 0;
      for (const b of boxes) b.share = b.o.id === "not_triaged" ? flow.nodes.get("not_triaged")?.share ?? null : triaged ? b.o.findings.length / triaged : null;
      const shown = new Set(stages.map((s) => s.id));
      const outs = new Set(boxes.map((b) => b.o.id));
      strands = flow.links.filter((l) => shown.has(l.parent) && (shown.has(l.child) || outs.has(l.child)));
      const nt = flow.nodes.get("not_triaged");
      if (nt && shown.has("not_triaged") && outs.has("not_triaged")) {
        for (const [sev, value] of Object.entries(nt.sev || {})) if (value > 0) strands.push({ parent: "not_triaged", child: "not_triaged", sev, value });
      }
      // A node's strands leave it in its children's order, worst first.
      const order = new Map([...stages.map((s, i) => [s.id, i]), ...boxes.map((b, i) => [b.o.id, 100 + i])]);
      const sevOrder = ["critical", "high", "medium", "low", "none", "unrated", "unknown"];
      strands.sort((a, b) => (order.get(a.child) ?? 0) - (order.get(b.child) ?? 0) || sevOrder.indexOf(a.sev) - sevOrder.indexOf(b.sev));
    }
  } else if (buf === FLEET) {
    if (group === "focus") ({ boxes, frames, cards: leading } = layoutFocus({ model, outcomes, store, ncols }));
    else if (groups) ({ frames, boxes } = layoutGroups({ model, store, groups, mode: group, aspect }));
    else boxes = layoutFlow({ model, store, ncols });
  }
  // What was put in this buffer by hand: machines, and outcome cards brought
  // out of the Findings.
  const byKind = buf === FLEET && group === "focus";
  for (const m of model.machines) {
    if (byKind) break;
    const at = store.boxes[m.id];
    if (!at || (at.buf || FLEET) !== buf || !Number.isFinite(at.x)) continue;
    boxes.push({ ...machineBox(m, at, model.findingsReady), x: at.x, y: at.y, placed: true });
  }
  if (buf !== FINDINGS && !byKind) {
    for (const o of outcomes) {
      const at = store.boxes[`o:${o.id}`];
      if (!at || at.buf !== buf || !Number.isFinite(at.x)) continue;
      boxes.push({ ...outcomeBox(o, at), x: at.x, y: at.y, placed: true });
    }
  }
  for (const b of boxes) if (b.kind === "machine") b.ready = model.findingsReady;
  const cards = [...leading];
  for (const [sid, at] of Object.entries(store.cards)) {
    if ((at.buf || FLEET) !== buf) continue;
    const ses = model.sessions.get(sid);
    if (!ses) continue;
    cards.push({ key: `c:${sid}`, sid, ses, from: at.from || null, x: at.x, y: at.y, s: at.s || 1, wu: SESSION_W, hu: sessionUnits(ses) });
  }
  const z = new Map(store.z.map((key, i) => [key, i]));
  const byZ = (a, b) => (z.get(a.key) ?? -1) - (z.get(b.key) ?? -1);
  boxes.sort(byZ);
  cards.sort(byZ);
  frames.sort(byZ);
  const rowBox = new Map();
  for (const b of boxes) for (const r of b.rows) if (!rowBox.has(r.s.id)) rowBox.set(r.s.id, b);
  return {
    buf, boxes, frames, stages, strands, cards, out, rowBox,
    byKey: new Map(boxes.map((b) => [b.key, b])),
    boxOf: new Map(boxes.filter((b) => b.kind === "machine").map((b) => [b.ep, b])),
    sessions: model.sessions,
  };
}

// What a card, a session or a dragged set of them is, to move it about.
const itemOf = (o, k) => (o.sid
  ? { key: o.key, kind: "session", sid: o.sid, s: o.s, zs: o.s * k, from: o.from || null }
  : { key: o.key, kind: o.kind, id: o.id, s: o.s, zs: o.s * k });
function placeItem(s, it, buf, x, y, scale) {
  if (it.kind === "session") {
    const at = s.cards[it.sid] || {};
    return { ...s, cards: { ...s.cards, [it.sid]: { ...at, buf, x, y, s: scale, from: at.from || it.from || null } } };
  }
  return { ...s, boxes: { ...s.boxes, [it.id]: { ...(s.boxes[it.id] || {}), buf, x, y, s: scale } } };
}

// -- a session's lanes -------------------------------------------------------------

// A session's lanes, read the way the workspace reads a session laid on it: its
// record, code-mode cells unwrapped and shell calls read for what they
// consulted, built into lanes, with when each turn happened. Read once its
// card is big enough on screen to show them, two at a time; the most recently
// shown few dozen kept.
const LANES = new Map();
const LANES_KEPT = 48;
const lanesWaiting = [];
let lanesReading = 0;
const lanesHeard = new Set();

function lanesOf(ses) {
  const hit = LANES.get(ses.id);
  if (hit) {
    LANES.delete(ses.id);
    LANES.set(ses.id, hit);
    return hit;
  }
  const entry = { status: "loading" };
  LANES.set(ses.id, entry);
  for (const [id, e] of LANES) {
    if (LANES.size <= LANES_KEPT) break;
    if (e.status !== "loading") LANES.delete(id);
  }
  lanesWaiting.push(ses);
  readNextLanes();
  return entry;
}
async function readLanes(ses) {
  let sinceMs = Number.isFinite(ses.firstMs) ? ses.firstMs - HOUR : null;
  if (sinceMs == null) {
    // An unbounded record read defaults to ninety days: the session's own
    // summary says when it began.
    const t = await query("getTrace", { id: ses.id }, { cache: true }).catch(() => null);
    const start = Number.isFinite(t?.firstTimestamp) ? t.firstTimestamp : t?.timestamp;
    if (Number.isFinite(start)) sinceMs = start - HOUR;
  }
  const page = await query("getSessionPage", { id: ses.id, sinceMs }, { cache: true });
  const raw = Array.isArray(page?.turns) ? page.turns : [];
  if (!raw.length) throw new Error("No record was read for it");
  await warmGrammar("javascript").catch(() => false);
  const turns = unwrapCodeModeTurns(raw);
  let extraItems = [];
  try { extraItems = (await readShellWork(turns))?.items || []; } catch { extraItems = []; }
  const model = buildTraceLanes(turns, extraItems.length ? { extraItems } : {});
  // Turns with no time to go by are spread over the session's span by their
  // order, or, with no span either, numbered: such a session draws its lanes
  // on its own clock and takes no part in a workspace's.
  const stamped = turnTimes(turns, { from: ses.firstMs, to: ses.lastMs });
  return { model, times: stamped || turns.map((_, i) => i), timed: !!stamped, truncated: page?.exhausted === false };
}
function readNextLanes() {
  while (lanesReading < 2 && lanesWaiting.length) {
    const ses = lanesWaiting.shift();
    const entry = LANES.get(ses.id);
    if (!entry || entry.status !== "loading") continue;
    lanesReading += 1;
    readLanes(ses).then(
      (r) => Object.assign(entry, { status: "done" }, r),
      (e) => Object.assign(entry, { status: "error", error: String(e?.message || e) }),
    ).finally(() => {
      lanesReading -= 1;
      for (const fn of lanesHeard) fn();
      readNextLanes();
    });
  }
}

// The lanes' names, in world units, in a rail before a session's time.
const RAIL_U = 150;
// The board's bucket: a column of cells this many pixels wide.
const CELL_PX = 7;
// A session's lanes drawn over x..x+w, y..y+h: the rail of their names, then
// its time in columns of cells, as many rows as fit open, the rest folded.
// The session's own clock fills the body.
function drawLanes(ctx, theme, entry, x, y, w, h, z) {
  const { model, times } = entry;
  const rail = Math.min(RAIL_U * z, w * 0.45);
  const bodyW = w - rail;
  if (bodyW < 24 || h < 16 || !model?.lanes?.length) return false;
  if (!entry.own) {
    const clock = buildClock(times);
    entry.own = clock ? { clock, tu: times.map((t) => clock.at(t)) } : null;
  }
  if (!entry.own) return false;
  const tu = entry.own.tu;
  const win = { u0: 0, u1: Math.max(1e-6, entry.own.clock.U) };
  const n = Math.max(1, Math.round(bodyW / CELL_PX));
  const key = `${Math.round(bodyW)}|${Math.round(h)}|${win.u0.toFixed(3)}|${win.u1.toFixed(3)}`;
  if (entry.sceneKey !== key) {
    if (!entry.shape) entry.shape = new Map(model.lanes.map((l) => [l.id, { id: l.id, label: l.label, domain: l.domain, cells: new Map() }]));
    const heightOf = (opts) => layoutRows(cardRows({ model, lanesById: entry.shape, ...opts })).height;
    const density = (g) => (g.lanes || []).reduce((a, l) => a + (l.cells?.size || 0), 0);
    const fit = fitRowsToHeight({ model, budget: h, density, heightOf });
    const lanes = bucketLanes(model.lanes, tu, win, n);
    const rows = cardRows({ model, lanesById: lanes, openGroups: fit.openGroups, only: fit.only });
    entry.scene = buildLaneScene({ rows, cellW: bodyW / n, width: bodyW, turnCount: n, railW: rail, sel: null, canSplice: false });
    entry.sceneKey = key;
  }
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  drawScene(ctx, entry.scene, { ...theme, bg: theme.panel }, { scrollX: 0, scrollY: 0, viewW: x + w, viewH: y + h, ox: x, oy: y, pinX: false, pinY: false, railH: h, upperGroups: false });
  ctx.restore();
  return true;
}

// -- painted images -----------------------------------------------------------------

// Each card's image, painted at the size it is on screen in one pane and
// drawn from there while the pane moves. The images together hold at most
// SPRITE_BUDGET device pixels; the least recently drawn go first.
const sprites = new Map();
let spritePx = 0;
const makeCanvas = (w, h) => {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
};
// Canvases let go of, kept to paint the next images on: making one costs more
// than painting a card, and a zoom changes every image's size at once. Each
// is made a little larger than asked for, so the sizes a zoom asks for next
// still fit it; an image is painted in its canvas's top left.
const spare = [];
let sparePx = 0;
const SPARE_PX = 8_000_000;
const BUCKET = 32;
const roomy = (c, pw, ph) => c.width >= pw && c.height >= ph && c.width <= pw * 1.5 + BUCKET && c.height <= ph * 1.5 + BUCKET;
function letGo(canvas) {
  const px = canvas.width * canvas.height;
  if (px > SPARE_PX) return;
  while (spare.length && sparePx + px > SPARE_PX) {
    const c = spare.shift();
    sparePx -= c.width * c.height;
  }
  spare.push(canvas);
  sparePx += px;
}
function canvasFor(old, pw, ph) {
  if (old && roomy(old, pw, ph)) return old;
  if (old) letGo(old);
  for (let i = spare.length - 1; i >= 0; i -= 1) {
    const c = spare[i];
    if (!roomy(c, pw, ph)) continue;
    sparePx -= c.width * c.height;
    spare.splice(i, 1);
    return c;
  }
  return makeCanvas(Math.ceil((pw * 1.25) / BUCKET) * BUCKET, Math.ceil((ph * 1.25) / BUCKET) * BUCKET);
}
function renderSprite(key, rev, w, h, dpr, paint) {
  const pw = Math.max(1, Math.ceil((w + 2 * SPRITE_PAD) * dpr));
  const ph = Math.max(1, Math.ceil((h + 2 * SPRITE_PAD) * dpr));
  if (pw * ph > SPRITE_MAX) return null;
  const old = sprites.get(key);
  if (old) {
    sprites.delete(key);
    spritePx -= old.px;
  }
  const canvas = canvasFor(old?.canvas, pw, ph);
  const px = canvas.width * canvas.height;
  if (spritePx + px > SPRITE_BUDGET) {
    for (const s of [...sprites.values()].sort((a, b) => a.at - b.at)) {
      if (spritePx + px <= SPRITE_BUDGET) break;
      sprites.delete(s.key);
      spritePx -= s.px;
      letGo(s.canvas);
    }
  }
  const sctx = canvas.getContext("2d");
  if (!sctx) return null;
  sctx.setTransform(1, 0, 0, 1, 0, 0);
  sctx.clearRect(0, 0, pw, ph);
  sctx.setTransform(dpr, 0, 0, dpr, SPRITE_PAD * dpr, SPRITE_PAD * dpr);
  const t0 = performance.now();
  paint(sctx);
  const at = performance.now();
  const sprite = { key, rev, w, h, dpr, canvas, pw, ph, px, at, cost: at - t0 };
  sprites.set(key, sprite);
  spritePx += px;
  return sprite;
}
function blit(ctx, sp, x, y, w, h) {
  const fx = w / sp.w;
  const fy = h / sp.h;
  ctx.drawImage(sp.canvas, 0, 0, sp.pw, sp.ph, x - SPRITE_PAD * fx, y - SPRITE_PAD * fy, (sp.pw / sp.dpr) * fx, (sp.ph / sp.dpr) * fy);
}
// A card at (x, y), w by h on screen: from its image when the image is of what
// it shows now at this size. Scaled from an image of another size while a
// zoom is under way (unless that is more than half again off), and, once
// this frame's time to repaint is spent, from any image of its shape, even
// one of what it showed before new data came; repainted when there is time.
// "late": no image to draw and no time to paint one, for the caller to draw
// the card's outline until a later frame. "none": too large to keep an image
// of, for the caller to draw itself.
let budgetFor = 0;
let paintedInBudget = 0;
function paintCached(ctx, key, rev, x, y, w, h, dpr, zooming, deadline, paint) {
  const sp = sprites.get(key);
  const t = performance.now();
  if (deadline !== budgetFor) {
    budgetFor = deadline;
    paintedInBudget = 0;
  }
  // Out of time, or out of it once this card is painted, going by what it
  // took last time; the first card of a frame is painted regardless.
  const late = t > deadline || (paintedInBudget > 0 && t + (sp?.cost ?? 1) > deadline);
  if (sp && sp.dpr === dpr) {
    if (sp.rev === rev && Math.abs(sp.w - w) < 0.5 && Math.abs(sp.h - h) < 0.5) {
      sp.at = t;
      blit(ctx, sp, x, y, w, h);
      return "exact";
    }
    const rw = w / sp.w;
    const rh = h / sp.h;
    if ((zooming && sp.rev === rev && rw > 0.67 && rw < 1.5) || (late && Math.abs(rw / rh - 1) < 0.2)) {
      sp.at = t;
      blit(ctx, sp, x, y, w, h);
      return "stale";
    }
  }
  if (late) return "late";
  paintedInBudget += 1;
  const made = renderSprite(key, rev, w, h, dpr, paint);
  if (!made) return "none";
  blit(ctx, made, x, y, w, h);
  return "painted";
}

// -- drawing -------------------------------------------------------------------------

// A card not painted yet: its outline, for the frame or two until it is.
function drawShell(ctx, theme, x, y, w, h) {
  roundRect(ctx, x, y, w, h, 3);
  ctx.fillStyle = theme.panel;
  ctx.fill();
  ctx.strokeStyle = theme.line;
  ctx.lineWidth = 1;
  ctx.stroke();
}

function sevColor(theme, rank) {
  if (rank < 0 || rank <= 1) return theme.danger;
  if (rank === 2) return theme.warn;
  if (rank === 3) return fade(theme.ink, 45);
  return null;
}
// The flow's severity ramp, worst darkest, as the Reviews lens draws it.
function strandColor(theme, sev) {
  return theme.sev[sev] || fade(theme.ink, 30);
}

// A colour at a share of its alpha, worked out once: the cards ask for the
// same few on every row and day they paint.
const FADES = new Map();
function fade(color, pct) {
  const key = `${color}|${pct}`;
  let v = FADES.get(key);
  if (v === undefined) {
    v = alphaMix(color, pct);
    if (FADES.size >= 4096) FADES.clear();
    FADES.set(key, v);
  }
  return v;
}

// Text measured once per string, at one size, and scaled: a text's width
// grows with its font's size, so a zoom to a size not seen before measures
// nothing new. A text too long for its room is cut by the widths of its
// beginnings, worked out once from its characters' (scaled to the whole's,
// kerning included), so a room not seen before measures nothing new either.
// The canvas helpers' own caches are sized for a plane's labels, and the
// cards' text outruns them.
const REF_PX = 64;
const ELLIPSIS = "\u2026";
const TEXT_CACHE_MAX = 40_000;
const CUTS_MAX = 8_000;
// Per weight: its font at REF_PX, its texts' and characters' widths there,
// its texts' beginnings, and its font at each size asked for. Keyed by the
// text alone, so painting builds no strings to look a width up.
const FACES = new Map();
function face(weight) {
  let f = FACES.get(weight);
  if (!f) {
    f = { ref: `${weight} ${REF_PX}px ${SANS}`, widths: new Map(), chars: new Map(), cuts: new Map(), fonts: new Map() };
    FACES.set(weight, f);
  }
  return f;
}
// Measured on a context of its own, so the one being drawn on keeps its font.
let measurer;
let measuring = null;
function measure(f, text) {
  if (measurer === undefined) measurer = makeCanvas(1, 1).getContext?.("2d") || null;
  if (!measurer) return text.length * REF_PX * 0.55;
  if (measuring !== f) {
    measurer.font = f.ref;
    measuring = f;
  }
  return measurer.measureText(text).width;
}
function fontOf(px, weight = 400) {
  const f = face(weight);
  let font = f.fonts.get(px);
  if (!font) {
    font = `${weight} ${px}px ${SANS}`;
    if (f.fonts.size >= 512) f.fonts.clear();
    f.fonts.set(px, font);
  }
  return font;
}
// How wide `text` is at `px` in `weight`.
function textW(text, px, weight = 400) {
  if (!text) return 0;
  const f = face(weight);
  let w = f.widths.get(text);
  if (w === undefined) {
    w = measure(f, text);
    if (f.widths.size >= TEXT_CACHE_MAX) f.widths.clear();
    f.widths.set(text, w);
  }
  return (w * px) / REF_PX;
}
// A text's beginnings, character by character, at REF_PX: where each ends in
// the string and how wide it is.
function cutsOf(f, text) {
  let c = f.cuts.get(text);
  if (c) return c;
  const ends = [];
  const widths = [];
  let sum = 0;
  let at = 0;
  for (const ch of text) {
    let a = f.chars.get(ch);
    if (a === undefined) {
      a = measure(f, ch);
      if (f.chars.size >= TEXT_CACHE_MAX) f.chars.clear();
      f.chars.set(ch, a);
    }
    sum += a;
    at += ch.length;
    ends.push(at);
    widths.push(sum);
  }
  const k = sum > 0 ? (f.widths.get(text) ?? measure(f, text)) / sum : 1;
  for (let i = 0; i < widths.length; i += 1) widths[i] *= k;
  c = { ends, widths };
  if (f.cuts.size >= CUTS_MAX) f.cuts.clear();
  f.cuts.set(text, c);
  return c;
}
// `text` cut to `maxW` at `px` in `weight`; how wide it is then is left in
// fitW, so painting a word makes no object to say so.
let fitW = 0;
function fit(text, maxW, px, weight = 400) {
  if (!text) {
    fitW = 0;
    return "";
  }
  const whole = textW(text, px, weight);
  if (whole <= maxW) {
    fitW = whole;
    return text;
  }
  const f = face(weight);
  const scale = px / REF_PX;
  const room = maxW / scale;
  const dots = textW(ELLIPSIS, REF_PX, weight);
  const { ends, widths } = cutsOf(f, text);
  // The longest beginning that fits with the ellipsis after it.
  let lo = 0;
  let hi = widths.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (widths[mid - 1] + dots <= room) lo = mid;
    else hi = mid - 1;
  }
  fitW = ((lo ? widths[lo - 1] : 0) + dots) * scale;
  return lo ? text.slice(0, ends[lo - 1]) + ELLIPSIS : ELLIPSIS;
}

// Words, cut to their room, at `alpha` of the current alpha; returns their
// width.
function words(ctx, text, x, y, px, maxW, color, { weight = 400, align = "left", alpha = 1 } = {}) {
  if (!text || maxW < 8 || alpha <= 0.01) return 0;
  ctx.font = fontOf(px, weight);
  const t = fit(text, maxW, px, weight);
  const w = fitW;
  const base = ctx.globalAlpha;
  ctx.globalAlpha = base * alpha;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  // Held to its room: a width scaled from another size can be a hair off.
  ctx.fillText(t, x, y, maxW);
  ctx.textAlign = "left";
  ctx.globalAlpha = base;
  return Math.min(w, maxW);
}

// A sizing grip in a corner: two short diagonals.
function grip(ctx, theme, x1, y1) {
  ctx.strokeStyle = theme.muted;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (const d of [4, 8]) {
    ctx.moveTo(x1 - 3, y1 - 3 - d);
    ctx.lineTo(x1 - 3 - d, y1 - 3);
  }
  ctx.stroke();
}

// A name on a tab the screen's size, for a card or frame too small to say it.
function tab(ctx, theme, text, x, y, maxW, { color, ring, weight = 500 }) {
  ctx.font = fontOf(11, weight);
  const t = fit(text, Math.max(24, maxW - 14), 11, weight);
  const lw = fitW + 12;
  roundRect(ctx, x, y, lw, 18, 2);
  ctx.fillStyle = theme.panel;
  ctx.fill();
  if (ring) {
    ctx.strokeStyle = ring;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.fillStyle = color || theme.ink;
  ctx.fillText(t, x + 6, y + 13, lw - 12);
  return lw;
}

const headOf = (b) => (b.kind === "outcome" ? OUT_HEAD_U : HEAD_U);

// A card's head, drawn within [vx0, vx1]: the card's width, or the part of it
// on screen when the card runs off its pane's side. A machine's name, whose
// it is and how much ran there, what met a standard on the right; an
// outcome's chip, what it holds and what that means.
function drawHead(ctx, theme, b, x, y, z, vx0, vx1, label) {
  const p = clamp(12 * z, 5, 14);
  const room = vx1 - vx0 - 2 * p;
  const hh = headOf(b) * z;
  const nameF = fs(13, z);
  const metaF = fs(11, z);
  const nameA = smooth(12, 16, hh);
  const metaA = smooth(30, 38, hh);
  if (nameA <= 0 || room <= 20) return;
  if (b.kind === "machine") {
    const { name, person } = label(b.ep);
    const single = y + hh / 2 + nameF * 0.34;
    const top = y + (hh - (nameF + 4 + metaF)) / 2;
    const nameY = lerp(single, top + nameF * 0.82, metaA);
    let right = vx1 - p;
    if (b.m.met && room > 150) {
      const said = `${b.m.met} met a standard`;
      const sw = textW(said, metaF);
      words(ctx, said, right, nameY, metaF, sw + 1, theme.danger, { align: "right", alpha: nameA });
      right -= sw + 12;
    }
    words(ctx, name, vx0 + p, nameY, nameF, right - vx0 - p, theme.ink, { weight: 500, alpha: nameA });
    const counts = [person, plural(b.m.sessions.length, "trace"), b.m.flagged.length ? `${b.m.flagged.length} flagged` : b.ready === false ? null : "nothing flagged"].filter(Boolean).join(DOT);
    words(ctx, counts, vx0 + p, top + nameF + 4 + metaF * 0.82, metaF, room, theme.muted, { alpha: metaA * nameA });
    return;
  }
  const o = b.o;
  const chipF = fs(10, z);
  const chip = o.label.toUpperCase();
  const pad = chipF * 0.8;
  const cw = Math.min(textW(chip, chipF, 600) + 2 * pad, room);
  const ch = chipF * 1.9;
  const top = y + (hh - (ch + 6 + metaF)) / 2;
  const cy = lerp(y + (hh - ch) / 2, top, metaA);
  ctx.globalAlpha = nameA;
  roundRect(ctx, vx0 + p, cy, cw, ch, 2);
  if ((o.base || o.id) === "met") {
    ctx.fillStyle = theme.ink;
    ctx.fill();
  } else {
    ctx.strokeStyle = (o.base || o.id) === "not_triaged" ? theme.line : theme.lineStrong;
    ctx.lineWidth = 1;
    if ((o.base || o.id) === "not_triaged") ctx.setLineDash([3, 2]);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.globalAlpha = 1;
  // Half a pixel over: a width added to and taken back from can come back a
  // hair short, and the label would lose its last letters.
  words(ctx, chip, vx0 + p + pad, cy + ch / 2 + chipF * 0.36, chipF, cw - 2 * pad + 0.5, (o.base || o.id) === "met" ? theme.panel : theme.muted, { weight: 600, alpha: nameA });
  const share = b.share != null ? pct(b.share) : "";
  if (share) words(ctx, share, vx1 - p, cy + ch / 2 + chipF * 0.36, metaF, 60, theme.muted, { align: "right", alpha: nameA });
  const said = [`${plural(o.groups.length, "trace")}${DOT}${plural(o.findings.length, "finding")}`, o.why].join("   ");
  words(ctx, said, vx0 + p, top + ch + 6 + metaF * 0.82, metaF, room, theme.muted, { alpha: metaA * nameA });
}

// A machine's card, or an outcome's, as its image holds it: its head, its
// strip and its rows, each saying as much as its size on screen allows.
// Anything pointed at, picked or lit is drawn over it (boxOverlay).
function drawCardBody(ctx, theme, b, x, y, z, { out, label, H = Infinity }) {
  const w = b.wu * z;
  const h = b.hu * z;
  const p = clamp(12 * z, 5, 14);
  ctx.save();
  ctx.shadowColor = theme.nodeShadow;
  ctx.shadowBlur = 10;
  ctx.shadowOffsetY = 2;
  roundRect(ctx, x, y, w, h, 3);
  ctx.fillStyle = theme.panel;
  ctx.fill();
  ctx.restore();
  roundRect(ctx, x, y, w, h, 3);
  ctx.strokeStyle = b.kind === "outcome" && (b.o.base || b.o.id) === "met" ? fade(theme.danger, 70) : theme.line;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  drawHead(ctx, theme, b, x, y, z, x, x + w, label);
  const hh = headOf(b) * z;
  // The strip: a machine's range day by day, a bar for how many sessions
  // began capped in the colour of the worst found that day; an outcome's
  // findings by severity, worst first.
  const sh = STRIP_U * z;
  if (sh >= 5) {
    const top = y + hh;
    const bx0 = x + p;
    const bw = w - 2 * p;
    if (b.kind === "machine") {
      const n = b.m.days.length;
      const step = bw / n;
      const max = Math.max(1, ...b.m.days);
      const barH = sh * 0.62;
      const base = top + sh * 0.78;
      ctx.fillStyle = fade(theme.ink, 10);
      ctx.fillRect(bx0, base, bw, 1);
      for (let d = 0; d < n; d += 1) {
        const c = b.m.days[d];
        if (!c) continue;
        const cap = sevColor(theme, b.m.dayWorst[d]);
        const bh = Math.max(1.5, Math.sqrt(c / max) * barH);
        const bxx = bx0 + d * step;
        const ww = Math.max(1, step - Math.max(1, step * 0.22));
        ctx.fillStyle = fade(theme.ink, 24);
        ctx.fillRect(bxx, base - bh, ww, bh);
        if (cap) {
          ctx.fillStyle = cap;
          ctx.fillRect(bxx, base - bh - Math.max(1.5, 2.5 * z), ww, Math.max(1.5, 2.5 * z));
        }
      }
    } else {
      const total = b.o.findings.length || 1;
      let sx = bx0;
      const by = top + sh * 0.32;
      const bh = Math.max(3, sh * 0.3);
      for (const sev of ["critical", "high", "medium", "low", "none", "unrated", "other"]) {
        const c = b.o.sev[sev] || 0;
        if (!c) continue;
        const ww = (c / total) * bw;
        ctx.fillStyle = strandColor(theme, sev);
        ctx.fillRect(sx, by, Math.max(1, ww - 1), bh);
        sx += ww;
      }
    }
    ctx.fillStyle = theme.lineSoft;
    ctx.fillRect(x + 1, top + sh - 1, w - 2, 1);
  }
  // Its rows.
  const titleF = fs(12, z);
  const subF = fs(11, z);
  const findF = fs(10.5, z);
  for (const r of b.rows) {
    const ry = y + r.y * z;
    const rh = r.h * z;
    if (ry > H || ry + rh < 0) continue;
    const s = r.s;
    const g = r.group || null;
    const worst = g ? g.worst : s.met.length ? -1 : s.worst;
    const color = sevColor(theme, g && g.findings.some((f) => f.met?.length) ? -1 : worst);
    const isOut = out.has(s.id);
    // Small, a row is a band of its colour; as it grows its words come in.
    const band = 1 - smooth(11, 18, rh);
    if (band > 0) {
      ctx.globalAlpha = band;
      ctx.fillStyle = color ? fade(color, 70) : fade(theme.ink, 12);
      ctx.fillRect(x + p, ry + Math.max(0.5, rh * 0.18), w - 2 * p, Math.max(1, rh * 0.64));
      ctx.globalAlpha = 1;
    }
    if (color) {
      ctx.fillStyle = color;
      ctx.fillRect(x + 1, ry + 1, Math.max(2, 3 * Math.min(1, z)), rh - 2);
    }
    const dim = isOut ? 0.45 : 1;
    const tA = smooth(14, 19, rh) * dim;
    if (tA > 0) {
      const lx = x + p + 4;
      const inner = w - 2 * p - 4;
      const pad = clamp(8 * z, 2, 10);
      const l1 = ry + Math.min(pad + titleF * 0.85, rh / 2 + titleF * 0.34);
      const when = dayText(g ? g.at || s.firstMs : s.firstMs);
      const whenW = inner > 220 ? textW(when, subF) : 0;
      if (whenW) words(ctx, when, x + w - p, l1, subF, whenW + 1, theme.mutedSoft, { align: "right", alpha: tA });
      const title = g ? `${g.findings[0].title || g.findings[0].category || "A finding"}${g.findings.length > 1 ? `  \u00d7${g.findings.length}` : ""}` : titleOf(s);
      words(ctx, title, lx, l1, titleF, inner - (whenW ? whenW + 10 : 0), r.kind === "clean" ? theme.muted : theme.ink, { weight: r.kind === "clean" ? 400 : 500, alpha: tA });
      const lines = [];
      if (r.kind === "flag") {
        lines.push([[plural(s.findings.length, "finding"), s.verified ? `${s.verified} verified` : "not verified yet"].join(DOT), theme.muted, s.met.length ? `Met ${s.met.join(", ")}` : ""]);
      } else if (g) {
        lines.push([[titleOf(s), label(s.endpointId).name].join(DOT), theme.muted, ""]);
        const met = [...new Set(g.findings.flatMap((f) => f.met || NONE))];
        lines.push([verdictMix(g.findings) || (g.findings[0].category ? short(g.findings[0].category, 60) : "not read yet"), theme.mutedSoft, met.length ? `Met ${met.join(", ")}` : ""]);
      }
      let ly = l1;
      for (const [text, c, red] of lines) {
        ly += 4 + subF;
        const lA = smooth(ly - ry + 2, ly - ry + 8, rh) * dim;
        if (lA <= 0) break;
        const used = words(ctx, text, lx, ly, subF, inner, c, { alpha: lA });
        if (red) words(ctx, `${DOT.trim()} ${red}`, lx + used + 6, ly, subF, inner - used - 6, theme.danger, { alpha: lA });
      }
      // Grown further, what was found, finding by finding.
      const list = g ? g.findings : r.kind === "flag" ? s.findings : NONE;
      let fy = ly + 5 + findF;
      for (const f of list) {
        if (fy > ry + rh - 4) break;
        const fA = smooth(fy + 2, fy + 8, ry + rh) * dim;
        if (fA <= 0) break;
        ctx.globalAlpha = fA;
        ctx.fillStyle = sevColor(theme, SEV_RANK[f.severity] ?? 9) || fade(theme.ink, 45);
        ctx.fillRect(lx, fy - findF * 0.62, findF * 0.55, findF * 0.55);
        ctx.globalAlpha = 1;
        const verdict = VERDICT_WORD[f.verdict] || "not verified yet";
        const vw = textW(verdict, findF);
        words(ctx, verdict, x + w - p, fy, findF, vw + 1, ASKABLE.has(f.verdict) ? theme.ink : theme.mutedSoft, { align: "right", alpha: fA });
        words(ctx, f.title || f.category || "A finding", lx + findF, fy, findF, inner - findF - vw - 10, f.met?.length ? theme.danger : theme.ink, { alpha: fA });
        fy += findF + 5;
      }
    }
    if (isOut) {
      const dash = clamp(3 * z, 1.5, 3);
      ctx.save();
      ctx.setLineDash([dash, dash]);
      ctx.strokeStyle = color || theme.lineStrong;
      ctx.lineWidth = 1;
      ctx.strokeRect(x + p + 0.5, ry + 1.5, w - 2 * p - 1, rh - 3);
      ctx.restore();
    }
    if (rh >= 10) {
      ctx.fillStyle = theme.lineSoft;
      ctx.fillRect(x + p, ry + rh - 0.5, w - 2 * p, 1);
    }
  }
  // Its foot: the rest, folded.
  if (b.foot) {
    const fy = y + b.foot.y * z;
    const fh = FOOT_U * z;
    const fA = smooth(12, 16, fh);
    const base = fy + fh / 2 + subF * 0.34;
    if (fA > 0) {
      const aw = b.foot.act ? textW(b.foot.act, subF) : 0;
      if (aw) words(ctx, b.foot.act, x + w - p, base, subF, aw + 1, theme.muted, { align: "right", alpha: fA });
      words(ctx, b.foot.said, x + p + 4, base, subF, w - 2 * p - aw - 16, theme.mutedSoft, { alpha: fA });
    } else {
      ctx.fillStyle = fade(theme.ink, 10);
      ctx.fillRect(x + p, fy + fh * 0.4, w - 2 * p, Math.max(1, fh * 0.2));
    }
  }
  ctx.restore();
}

// What a card's name says on its tab when the card is too small to say it.
function tabOf(b, theme, label) {
  if (b.kind === "machine") {
    const { name } = label(b.ep);
    return { text: `${name}${b.m.met ? `${DOT}${b.m.met} met` : ""}`, color: b.m.met ? theme.danger : theme.ink };
  }
  return { text: `${b.o.label}${DOT}${num(b.o.findings.length)}`, color: (b.o.base || b.o.id) === "met" ? theme.danger : theme.ink };
}

// Where a card is pointed at, picked or lit from another pane: the rows
// pointed at or lit, its outline, its head held in view when it runs off its
// pane's side, its name on a tab when it is too small to say it, and its
// handles. The tab's hit goes to `hits`.
function boxOverlay(ctx, theme, b, x, y, z, { W, pid, hot, picked, hoverRow, footHot, edgeHot, handling, focus, label, hits }) {
  const w = b.wu * z;
  const h = b.hu * z;
  const p = clamp(12 * z, 5, 14);
  for (const r of b.rows) {
    const lit = focus?.sid && r.s.id === focus.sid && !(focus.src === pid && focus.key === b.key);
    const pointed = hoverRow === r.s.id;
    if (!lit && !pointed) continue;
    ctx.fillStyle = lit ? theme.greenSoft : fade(theme.ink, 6);
    ctx.fillRect(x + 1, y + r.y * z, w - 2, r.h * z);
  }
  if (footHot && b.foot?.act) {
    const fh = FOOT_U * z;
    const subF = fs(11, z);
    const fA = smooth(12, 16, fh);
    if (fA > 0) {
      words(ctx, b.foot.act, x + w - p, y + b.foot.y * z + fh / 2 + subF * 0.34, subF, textW(b.foot.act, subF) + 1, theme.ink, { align: "right", alpha: fA });
    }
  }
  const litCard = focus?.ep && b.kind === "machine" && b.ep === focus.ep && focus.key !== b.key;
  if (picked || hot || litCard) {
    roundRect(ctx, x, y, w, h, 3);
    ctx.strokeStyle = picked ? theme.ink : litCard ? theme.green : theme.lineStrong;
    ctx.lineWidth = picked || litCard ? 1.5 : 1;
    ctx.stroke();
  }
  const vx0 = Math.max(x, 0);
  const vx1 = Math.min(x + w, W);
  const hh = headOf(b) * z;
  const nameA = smooth(12, 16, hh);
  if (nameA > 0 && (x < 0 || x + w > W) && vx1 - vx0 > 40) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(vx0, y + 1, vx1 - vx0, hh - 1);
    ctx.clip();
    ctx.fillStyle = theme.panel;
    ctx.fillRect(vx0, y + 1, vx1 - vx0, hh - 1);
    drawHead(ctx, theme, b, x, y, z, vx0, vx1, label);
    ctx.restore();
  }
  if (nameA < 1 && vx1 - vx0 >= 36 && !(b.frame && z < 0.16)) {
    const { text, color } = tabOf(b, theme, label);
    ctx.globalAlpha = 1 - nameA;
    const top = Math.min(Math.max(y + 1, 1), y + h - 19);
    const lw = tab(ctx, theme, text, vx0 + 1, top, vx1 - vx0, { color, ring: picked ? theme.ink : litCard ? theme.green : hot ? theme.lineStrong : null });
    ctx.globalAlpha = 1;
    hits.push({ type: "box-head", key: b.key, x0: vx0 + 1, y0: y, x1: vx0 + 1 + lw, y1: y + 19 });
  }
  if (hot || picked || handling) grip(ctx, theme, x + w, y + h);
  if (edgeHot || handling === "width") {
    ctx.fillStyle = theme.lineStrong;
    ctx.fillRect(x + w - 1, y + 3, 2, h - 6);
  }
}

// What a card answers to under the pointer: itself, its head, its rows, its
// foot (the tab's is pushed with its overlay, its edge and corner after).
function boxHits(hits, b, x, y, z, W, H) {
  const w = b.wu * z;
  const h = b.hu * z;
  hits.push({ type: "box", key: b.key, x0: x, y0: y, x1: x + w, y1: y + h });
  const p = clamp(12 * z, 5, 14);
  const hh = headOf(b) * z;
  if (smooth(12, 16, hh) > 0 && Math.min(x + w, W) - Math.max(x, 0) - 2 * p > 20) hits.push({ type: "box-head", key: b.key, x0: x, y0: y, x1: x + w, y1: y + hh });
  for (const r of b.rows) {
    const ry = y + r.y * z;
    const rh = r.h * z;
    if (ry > H || ry + rh < 0) continue;
    hits.push({ type: "row", key: b.key, sid: r.s.id, x0: x, y0: ry, x1: x + w, y1: ry + rh });
  }
  if (b.foot?.act) {
    const fy = y + b.foot.y * z;
    hits.push({ type: "foot", key: b.key, x0: x, y0: fy, x1: x + w, y1: fy + FOOT_U * z });
  }
}

// A group's frame: its name, what it holds, and (for work) the topics its
// machines share, on a ground a step off the pane's.
// A frame may say its own line (`said`), carry the danger tone, and stay
// where its layout puts it (`fixed`: nothing to drag by).
function drawFrame(ctx, theme, f, x, y, z, { hover, picked, moving, W, hits, mode }) {
  const w = f.w * z;
  const h = f.h * z;
  const hot = hover?.key === f.key;
  roundRect(ctx, x, y, w, h, 4);
  ctx.fillStyle = fade(f.tone === "danger" ? theme.danger : theme.ink, moving ? 6 : 3);
  ctx.fill();
  ctx.strokeStyle = f.tone === "danger" ? fade(theme.danger, 55) : picked ? theme.ink : hot || moving ? theme.lineStrong : theme.line;
  ctx.lineWidth = picked ? 1.5 : 1;
  ctx.stroke();
  const hh = FRAME_HEAD * z;
  const p = clamp(FRAME_PAD * z, 6, 18);
  const vx0 = Math.max(x, 0);
  const vx1 = Math.min(x + w, W);
  const room = vx1 - vx0 - 2 * p;
  const nameF = fs(14, z);
  const metaF = fs(11, z);
  const nameA = smooth(16, 22, hh);
  const metaA = smooth(28, 36, hh);
  const g = f.g;
  const counts = f.said ?? [
    plural(f.count, "machine"),
    mode === "team" && g.people?.length ? plural(g.people.length, "person", "people") : null,
    f.flagged ? `${num(f.flagged)} flagged` : null,
  ].filter(Boolean).join(DOT);
  if (nameA > 0 && room > 30) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, hh);
    ctx.clip();
    const nameY = y + clamp(12 * z, 5, 15) + nameF * 0.8;
    let right = vx1 - p;
    if (f.met && room > 160) {
      const said = `${f.met} met a standard`;
      const sw = textW(said, metaF);
      words(ctx, said, right, nameY, metaF, sw + 1, theme.danger, { align: "right", alpha: nameA });
      right -= sw + 14;
    }
    words(ctx, g.label, vx0 + p, nameY, nameF, right - vx0 - p, theme.ink, { weight: 500, alpha: nameA });
    // What it holds, and the topics its machines' work falls in.
    const ly = nameY + 4 + metaF;
    const used = words(ctx, counts, vx0 + p, ly, metaF, room, theme.muted, { alpha: metaA * nameA });
    if (g.topics?.length && metaA > 0) {
      let tx = vx0 + p + used + 14;
      for (const [i, t] of g.topics.entries()) {
        const text = `${t.name} ${pct(t.share)}`;
        const tw = textW(text, metaF);
        if (tx + metaF + tw > vx1 - p) break;
        ctx.globalAlpha = metaA * nameA;
        ctx.fillStyle = CLUSTER_HUES[i % CLUSTER_HUES.length];
        ctx.fillRect(tx, ly - metaF * 0.62, metaF * 0.6, metaF * 0.6);
        ctx.globalAlpha = 1;
        words(ctx, text, tx + metaF * 0.6 + 5, ly, metaF, tw + 1, theme.mutedSoft, { alpha: metaA * nameA });
        tx += metaF * 0.6 + 5 + tw + 12;
      }
    } else if (mode === "team" && g.people?.length && metaA > 0) {
      words(ctx, g.people.map(personName).join(", "), vx0 + p + used + 14, ly, metaF, room - used - 14, theme.mutedSoft, { alpha: metaA * nameA });
    }
    ctx.restore();
  }
  if (nameA < 1 && vx1 - vx0 >= 40) {
    ctx.globalAlpha = 1 - nameA;
    const ty = Math.min(Math.max(y - 20, 1), y + h - 19);
    const lw = tab(ctx, theme, f.said ? g.label : `${g.label}${DOT}${plural(f.count, "machine")}`, vx0 + 1, ty, Math.max(140, vx1 - vx0), { color: f.met || f.tone === "danger" ? theme.danger : theme.ink, ring: picked ? theme.ink : theme.lineStrong, weight: 600 });
    ctx.globalAlpha = 1;
    if (!f.fixed) hits.push({ type: "frame-head", key: f.key, x0: vx0 + 1, y0: ty, x1: vx0 + 1 + lw, y1: ty + 18 });
  }
  if (!f.fixed) hits.push({ type: "frame-head", key: f.key, x0: x, y0: y, x1: x + w, y1: y + Math.max(hh, 20) });
}

// A node of the flow: its column's caption, its name, how many findings it
// holds and its share of the node it came from, its severities as a bar.
function drawStage(ctx, theme, st, x, y, z, { hover, hits }) {
  const w = st.w * z;
  const h = st.h * z;
  const node = st.node;
  const drop = node.kind === "drop";
  const hot = hover?.key === st.key;
  ctx.save();
  ctx.shadowColor = theme.nodeShadow;
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 1;
  roundRect(ctx, x, y, w, h, 3);
  ctx.fillStyle = drop ? overMix(theme.ink, 2, theme.bg) : theme.panel;
  ctx.fill();
  ctx.restore();
  roundRect(ctx, x, y, w, h, 3);
  ctx.strokeStyle = hot ? theme.lineStrong : theme.line;
  ctx.lineWidth = 1;
  ctx.stroke();
  const p = clamp(12 * z, 5, 14);
  const capF = fs(9.5, z);
  const nameF = fs(drop ? 11.5 : 12.5, z);
  const countF = fs(drop ? 13 : 18, z);
  const barY = y + h - Math.max(4, p * 0.7) - 4;
  const room = barY - (y + p) - 2;
  const withCaption = !!st.caption && !drop && capF + nameF + countF + 10 <= room;
  const stacked = nameF + countF + 6 <= room;
  const aA = smooth(18, 28, h);
  if (aA > 0) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    let ly = y + p;
    if (withCaption) {
      ly += capF * 0.85;
      words(ctx, st.caption.toUpperCase(), x + p, ly, capF, w - 2 * p, theme.eyebrow, { weight: 500, alpha: aA });
      ly += 4;
    }
    ly += nameF * 0.85;
    const nw = words(ctx, node.label, x + p, ly, nameF, w - 2 * p, drop ? theme.muted : theme.ink, { weight: 500, alpha: aA });
    const count = num(node.count);
    if (stacked) {
      const cy = ly + 5 + countF * 0.85;
      const cw = words(ctx, count, x + p, cy, countF, w - 2 * p, drop ? theme.muted : theme.ink, { weight: drop ? 400 : 500, alpha: aA });
      if (node.share != null) words(ctx, pct(node.share), x + p + cw + 8, cy, fs(11, z), 80, theme.muted, { alpha: aA });
    } else {
      words(ctx, `${count}${node.share != null ? `  ${pct(node.share)}` : ""}`, x + p + nw + 8, ly, nameF, w - 2 * p - nw - 8, theme.muted, { alpha: aA });
    }
    ctx.restore();
  }
  // Its severities, worst first.
  const total = Object.values(node.sev || {}).reduce((n, v) => n + v, 0);
  if (total > 0 && !drop) {
    let sx = x + p;
    const bw = w - 2 * p;
    for (const sev of ["critical", "high", "medium", "low", "none", "unrated", "unknown"]) {
      const v = node.sev[sev] || 0;
      if (!v) continue;
      const ww = (v / total) * bw;
      ctx.fillStyle = strandColor(theme, sev);
      ctx.fillRect(sx, barY, Math.max(1, ww - 0.5), 4);
      sx += ww;
    }
  }
  hits.push({ type: "stage", key: st.key, x0: x, y0: y, x1: x + w, y1: y + h });
}

// The flow's strands: each node's links leave its right edge child by child,
// worst severity first within each, and enter the child's left edge as a
// slice of its own band; between, a strand as wide as its share.
function drawStrands(ctx, theme, strands, rectOf) {
  const byParent = new Map();
  for (const l of strands) {
    if (!byParent.has(l.parent)) byParent.set(l.parent, []);
    byParent.get(l.parent).push(l);
  }
  const into = new Map();
  for (const l of strands) into.set(l.child, (into.get(l.child) || 0) + l.value);
  const used = new Map();
  for (const [parent, list] of byParent) {
    const a = rectOf(parent);
    if (!a) continue;
    const total = list.reduce((n, l) => n + l.value, 0);
    if (!(total > 0)) continue;
    let ay = a.y + a.bandTop;
    for (const l of list) {
      const c = rectOf(l.child);
      if (!c) continue;
      const ay1 = ay + Math.max(0.6, (l.value / total) * a.band);
      const cIn = into.get(l.child) || l.value;
      const by0 = c.y + c.bandTop + (used.get(l.child) || 0);
      const by1 = by0 + Math.max(0.6, (l.value / cIn) * c.band);
      used.set(l.child, by1 - c.y - c.bandTop);
      const x0 = a.x + a.w;
      const x1 = c.x;
      const xm = (x0 + x1) / 2;
      ctx.beginPath();
      ctx.moveTo(x0, ay);
      ctx.bezierCurveTo(xm, ay, xm, by0, x1, by0);
      ctx.lineTo(x1, by1);
      ctx.bezierCurveTo(xm, by1, xm, ay1, x0, ay1);
      ctx.closePath();
      ctx.fillStyle = fade(strandColor(theme, l.sev), l.sev === "unknown" ? 16 : l.sev === "none" || l.sev === "unrated" ? 22 : 46);
      ctx.fill();
      ay = ay1;
    }
  }
}

// A session's foot: its actions. Zoom in fills the pane with it, its lanes
// open as far as the room allows.
const FOOT_ACTS = [["card-close", "Put it back"], ["card-zoom", "Zoom in"]];
// A session leading the first page: its own workspace (its board, in a pane
// beside), or zoom in.
const FOOT_ACTS_LEAD = [["card-board", "Open investigation"], ["card-zoom", "Zoom in"]];

function sessionFoot(x, y, w, h, z, lead = false) {
  const subF = fs(11, z);
  const fh = 30 * Math.min(1, z);
  const footY = y + h - fh;
  const acts = [];
  let ax = x + w;
  for (const [type, text] of lead ? FOOT_ACTS_LEAD : FOOT_ACTS) {
    const aw = textW(text, subF) + 18;
    ax -= aw;
    if (ax < x + 4) break;
    acts.push({ type, text, x0: ax, x1: ax + aw });
  }
  return { footY, fh, subF, acts };
}

// A session out of its card, its top left at (x, y) on screen at scale `z`:
// as many of its lines as its size on screen holds.
function drawSessionBody(ctx, theme, s, x, y, z, { label, lifted = false, on = null, lanes = null }) {
  const w = (on?.wu || SESSION_W) * z;
  const h = (on?.hu || sessionUnits(s)) * z;
  ctx.save();
  ctx.shadowColor = lifted ? "rgba(0, 0, 0, 0.35)" : theme.nodeShadowLift;
  ctx.shadowBlur = lifted ? 18 : 12;
  ctx.shadowOffsetY = 4;
  roundRect(ctx, x, y, w, h, 3);
  ctx.fillStyle = theme.panel;
  ctx.fill();
  ctx.restore();
  const color = sevColor(theme, s.met.length ? -1 : s.worst);
  roundRect(ctx, x, y, w, h, 3);
  ctx.strokeStyle = s.met.length ? theme.danger : theme.lineStrong;
  ctx.lineWidth = s.met.length ? 1.5 : 1;
  ctx.stroke();
  if (color) {
    ctx.fillStyle = color;
    ctx.fillRect(x + 1, y + 1, Math.max(2, 3 * Math.min(1, z)), h - 2);
  }
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  const p = clamp(12 * z, 5, 14);
  const titleF = fs(12.5, z);
  const subF = fs(11, z);
  const bottom = y + h - (h >= 120 ? 30 * Math.min(1, z) : 4);
  const lx = x + p + 2;
  const inner = w - 2 * p - 2;
  let ly = y + clamp(10 * z, 3, 10) + titleF * 0.85;
  const fits = (lineH) => ly + lineH * 0.2 <= bottom;
  const { name, person } = label(s.endpointId);
  words(ctx, titleOf(s), lx, ly, titleF, inner - 22, theme.ink, { weight: 500 });
  if (h >= 22 && w >= 120 && !on?.hero) words(ctx, "\u00d7", x + w - p, ly, 15, 16, theme.muted, { align: "right" });
  const lines = [
    [[name, person, agentName(s.agent)].filter(Boolean).join(DOT), theme.muted],
    [[whenText(s.firstMs), lengthText((s.lastMs || 0) - (s.firstMs || 0)), s.requests ? plural(s.requests, "request") : null].filter(Boolean).join(DOT), theme.muted],
  ];
  for (const [text, c] of lines) {
    ly += subF + 5;
    if (!fits(subF)) break;
    words(ctx, text, lx, ly, subF, inner, c);
  }
  ly += 8;
  if (fits(subF)) {
    ctx.fillStyle = theme.lineSoft;
    ctx.fillRect(x + 1, ly - 2, w - 2, 1);
  }
  // Grown far enough, its lanes: what was found said in a line over them.
  if (lanes && fits(subF + 60)) {
    if (s.findings.length) {
      ly += subF + 6;
      const met = s.met.length ? `Met ${s.met.join(", ")}` : "";
      const said = [plural(s.findings.length, "finding"), s.verified ? `${s.verified} verified` : "not verified yet"].join(DOT);
      const used = words(ctx, said, lx, ly, subF, inner, theme.muted);
      if (met) words(ctx, `${DOT.trim()} ${met}`, lx + used + 6, ly, subF, inner - used - 6, theme.danger);
    }
    const top = ly + 8;
    const lh = bottom - top - 4;
    if (lanes.status === "done") drawLanes(ctx, theme, lanes, x + 1, top, w - 2, lh, z);
    else words(ctx, lanes.status === "error" ? "Its record could not be read" : "Reading its record\u2026", lx, top + subF + 4, subF, inner, theme.mutedSoft);
    ctx.restore();
    if (h >= 120) drawSessionFoot(ctx, theme, x, y, w, h, z, on);
    return;
  }
  if (!s.findings.length && fits(subF + 10)) {
    ly += subF + 8;
    words(ctx, "The review found nothing in it", lx, ly, subF, inner, theme.muted);
  }
  for (const f of s.findings) {
    ly += subF + 9;
    if (!fits(subF)) break;
    ctx.fillStyle = sevColor(theme, SEV_RANK[f.severity] ?? 9) || fade(theme.ink, 45);
    ctx.fillRect(lx, ly - subF * 0.66, subF * 0.62, subF * 0.62);
    const verdict = VERDICT_WORD[f.verdict] || "not verified yet";
    const vw = textW(verdict, subF);
    words(ctx, verdict, x + w - p, ly, subF, vw + 1, ASKABLE.has(f.verdict) ? theme.ink : theme.mutedSoft, { align: "right" });
    words(ctx, f.title || f.category || "A finding", lx + subF + 2, ly, subF, inner - subF - vw - 14, theme.ink);
    if (f.met?.length) {
      ly += subF + 4;
      if (!fits(subF)) break;
      words(ctx, `Met ${f.met.join(", ")}`, lx + subF + 2, ly, subF, inner - subF, theme.danger);
    }
  }
  ctx.restore();
  if (h >= 120) drawSessionFoot(ctx, theme, x, y, w, h, z, on);
}
function drawSessionFoot(ctx, theme, x, y, w, h, z, on) {
  const f = sessionFoot(x, y, w, h, z, !!on?.hero);
  ctx.fillStyle = theme.lineSoft;
  ctx.fillRect(x + 1, f.footY, w - 2, 1);
  for (const a of f.acts) {
    ctx.fillStyle = theme.lineSoft;
    ctx.fillRect(a.x0, f.footY + 1, 1, f.fh - 1);
    words(ctx, a.text, a.x0 + 9, f.footY + f.fh / 2 + f.subF * 0.34, f.subF, a.x1 - a.x0, theme.muted);
  }
}

// What a session's card answers to under the pointer.
function sessionHits(hits, c, x, y, z) {
  const w = c.wu * z;
  const h = c.hu * z;
  hits.push({ type: "card", key: c.key, sid: c.sid, x0: x, y0: y, x1: x + w, y1: y + h });
  if (h >= 120) {
    const f = sessionFoot(x, y, w, h, z, !!c.hero);
    for (const a of f.acts) hits.push({ type: a.type, key: c.key, x0: a.x0, y0: f.footY, x1: a.x1, y1: y + h });
  }
  if (h >= 22 && w >= 120 && !c.hero) {
    const p = clamp(12 * z, 5, 14);
    hits.push({ type: "card-close", key: c.key, x0: x + w - p - 18, y0: y + 2, x1: x + w - 2, y1: y + 24 });
  }
  hits.push({ type: "card-corner", key: c.key, x0: x + w - GRIP, y0: y + h - GRIP, x1: x + w + 4, y1: y + h + 4 });
}

// Where a session's card is pointed at, picked or lit from another pane.
function sessionOverlay(ctx, theme, c, x, y, z, { hover, picked, focus, pid, handling }) {
  const w = c.wu * z;
  const h = c.hu * z;
  const p = clamp(12 * z, 5, 14);
  if (hover?.key === c.key && hover.type === "card-close" && h >= 22 && w >= 120 && !c.hero) {
    words(ctx, "\u00d7", x + w - p, y + clamp(10 * z, 3, 10) + fs(12.5, z) * 0.85, 15, 16, theme.ink, { align: "right" });
  }
  if (hover?.key === c.key && h >= 120) {
    const f = sessionFoot(x, y, w, h, z, !!c.hero);
    for (const a of f.acts) if (a.type === hover.type) words(ctx, a.text, a.x0 + 9, f.footY + f.fh / 2 + f.subF * 0.34, f.subF, a.x1 - a.x0, theme.ink);
  }
  const lit = focus?.sid === c.sid && !(focus.src === pid && focus.key === c.key);
  if (picked || lit) {
    roundRect(ctx, x, y, w, h, 3);
    ctx.strokeStyle = picked ? theme.ink : theme.green;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
  if (hover?.key === c.key || picked || handling) grip(ctx, theme, x + w, y + h);
}

// Where a session sits in the card holding it on screen: its row, or the
// card's foot (or head) while its row is folded away.
function rowAnchor(b, sid, k, ox, oy) {
  const z = k * b.s;
  const x = b.x * k + ox;
  const y = b.y * k + oy;
  const r = b.rows.find((row) => row.s.id === sid);
  const at = r ? y + (r.y + r.h / 2) * z : b.foot ? y + (b.foot.y + FOOT_U / 2) * z : y + (headOf(b) / 2) * z;
  return { x: x + (b.wu * z) / 2, x0: x, x1: x + b.wu * z, y: at, rowY: r ? y + r.y * z : at, rowH: r ? r.h * z : 0 };
}

// A tie from a row to the card it was pulled out into.
function tie(ctx, a, r, color) {
  const tx = r.x >= a.x ? a.x1 : a.x0;
  const cx = r.x >= a.x ? r.x : r.x + r.w;
  const cy = clamp(a.y, r.y + 14, r.y + r.h - 14);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.25;
  ctx.beginPath();
  ctx.moveTo(tx, a.y);
  const mx = (tx + cx) / 2;
  ctx.bezierCurveTo(mx, a.y, mx, cy, cx, cy);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(tx, a.y, 2.5, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

// A pane's frame: its buffer's frames, flow, cards and sessions, from their
// images where it can, with what is pointed at, picked or lit drawn over
// them. Returns where everything answers to the pointer, where its rows and
// sessions are for ties across panes, and whether it needs another frame.
function paintPane(ctx, S) {
  const { W, H, dpr, view, theme, scene, label, pid, revBase, out, hover, sel, lifted, focus, flights, now, zooming, glide, local, mode } = S;
  const deadline = S.deadline ?? performance.now() + REPAINT_MS;
  const { k, ox, oy } = view;
  const SX = (x) => x * k + ox;
  const SY = (y) => y * k + oy;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, W, H);
  const dots = getDotTilePattern(ctx, { color: theme.diagramDot, size: 7, dotX: 3.5, dotY: 3.5, dotRadius: 0.9 });
  if (dots) {
    ctx.save();
    ctx.translate(((ox % 7) + 7) % 7, ((oy % 7) + 7) % 7);
    ctx.fillStyle = dots;
    ctx.fillRect(-7, -7, W + 14, H + 14);
    ctx.restore();
  }
  const hits = [];
  const anchors = { rows: new Map(), cards: [] };
  let again = false;
  let stale = false;
  // Where each card and frame is this frame: gliding to where the layout puts
  // it, or where the hand holds it.
  const held = (o) => Boolean(local?.moved && ((local.kind === "frame" && (o.key === local.key || o.frame === local.key)) || ((local.kind === "scale" || local.kind === "width") && o.key === local.key)));
  const at = (o) => {
    const was = glide.get(o.key);
    if (!was || held(o) || document.hidden) {
      glide.set(o.key, { x: o.x, y: o.y });
      return o;
    }
    const dx = o.x - was.x;
    const dy = o.y - was.y;
    if (Math.abs(dx) + Math.abs(dy) < 0.5) {
      glide.set(o.key, { x: o.x, y: o.y });
      return o;
    }
    again = true;
    const p = { x: was.x + dx * 0.22, y: was.y + dy * 0.22 };
    glide.set(o.key, p);
    return { ...o, ...p };
  };
  const frames = scene.frames.map(at);
  const boxes = scene.boxes.map(at);
  const byKey = new Map(boxes.map((b) => [b.key, b]));

  for (const f of frames) {
    const x = SX(f.x);
    const y = SY(f.y);
    if (x > W || y > H || x + f.w * k < 0 || y + f.h * k < 0) continue;
    drawFrame(ctx, theme, f, x, y, k, { hover, picked: sel.has(f.key), moving: held(f), W, hits, mode });
  }

  // The flow's strands, under its nodes and the outcome cards they feed.
  if (scene.stages.length || scene.strands.length) {
    const rects = new Map();
    for (const st of scene.stages) rects.set(st.id, { x: SX(st.x), y: SY(st.y), w: st.w * k, h: st.h * k, bandTop: st.h * k * 0.18, band: st.h * k * 0.64 });
    for (const b of boxes) {
      if (b.kind !== "outcome" || lifted?.has(b.key)) continue;
      const z = k * b.s;
      rects.set(b.o.id, { x: SX(b.x), y: SY(b.y), w: b.wu * z, h: b.hu * z, bandTop: 6 * z, band: (OUT_HEAD_U - 12) * z });
    }
    drawStrands(ctx, theme, scene.strands, (id) => rects.get(id));
    for (const st of scene.stages) {
      const x = SX(st.x);
      const y = SY(st.y);
      if (x > W || y > H || x + st.w * k < 0 || y + st.h * k < 0) continue;
      drawStage(ctx, theme, st, x, y, k, { hover, hits });
    }
  }

  // The cards.
  for (const b of boxes) {
    if (lifted?.has(b.key)) continue;
    const z = k * b.s;
    const x = SX(b.x);
    const y = SY(b.y);
    const w = b.wu * z;
    const h = b.hu * z;
    if (x > W || y > H || x + w < 0 || y + h < 0) continue;
    const outs = b.rows.filter((r) => out.has(r.s.id)).map((r) => r.s.id).join(",");
    const rev = `${revBase}|${b.open ? 1 : 0}|${b.wu}|${b.ready === false ? 0 : 1}|${b.share ?? ""}|${outs}`;
    const painted = paintCached(ctx, `${pid}|${b.key}`, rev, x, y, w, h, dpr, zooming, deadline, (c) => drawCardBody(c, theme, b, 0, 0, z, { out, label }));
    if (painted === "stale") stale = true;
    else if (painted === "late") {
      drawShell(ctx, theme, x, y, w, h);
      stale = true;
    } else if (painted === "none") drawCardBody(ctx, theme, b, x, y, z, { out, label, H });
    boxHits(hits, b, x, y, z, W, H);
    for (const r of b.rows) {
      const ry = y + r.y * z;
      const my = ry + (r.h * z) / 2;
      if (my < 0 || my > H || x + w < 0 || x > W) continue;
      anchors.rows.set(`${b.key}|${r.s.id}`, { x0: Math.max(x, 0), x1: Math.min(x + w, W), y: my });
    }
    const handling = local?.moved && (local.kind === "scale" || local.kind === "width") && local.key === b.key ? local.kind : null;
    boxOverlay(ctx, theme, b, x, y, z, {
      W, pid, label, hits, focus, handling,
      hot: hover?.key === b.key,
      picked: sel.has(b.key),
      hoverRow: hover?.type === "row" && hover.key === b.key ? hover.sid : null,
      footHot: hover?.type === "foot" && hover.key === b.key,
      edgeHot: hover?.type === "box-edge" && hover.key === b.key,
    });
    hits.push({ type: "box-edge", key: b.key, x0: x + w - EDGE, y0: y + Math.min(headOf(b) * z, h / 3), x1: x + w + EDGE, y1: y + h - GRIP });
    hits.push({ type: "box-corner", key: b.key, x0: x + w - GRIP, y0: y + h - GRIP, x1: x + w + 4, y1: y + h + 4 });
  }

  // The sessions out of their cards, tied to the rows they came from when
  // those are in this pane.
  const flying = new Set(flights.map((f) => f.sid));
  for (const c of scene.cards) {
    if (lifted?.has(c.key) || flying.has(c.sid)) continue;
    const src = c.from ? byKey.get(c.from) : byKey.get(scene.rowBox.get(c.sid)?.key) || byKey.get(scene.boxOf.get(c.ses.endpointId)?.key);
    if (!src || lifted?.has(src.key)) continue;
    const a = rowAnchor(src, c.sid, k, ox, oy);
    tie(ctx, a, { x: SX(c.x), y: SY(c.y), w: c.wu * k * c.s, h: c.hu * k * c.s }, sevColor(theme, c.ses.met.length ? -1 : c.ses.worst) || theme.lineStrong);
  }
  for (const c of scene.cards) {
    if (lifted?.has(c.key) || flying.has(c.sid)) continue;
    const x = SX(c.x);
    const y = SY(c.y);
    const z = k * c.s;
    const w = c.wu * z;
    const h = c.hu * z;
    if (x > W || y > H || x + w < 0 || y + h < 0) continue;
    // Grown well past its card on screen, a session shows its lanes.
    const lanes = h >= 170 && w >= 460 ? lanesOf(c.ses) : null;
    const painted = paintCached(ctx, `${pid}|${c.key}`, `${revBase}|s|${lanes ? lanes.status : "-"}`, x, y, w, h, dpr, zooming, deadline, (cx) => drawSessionBody(cx, theme, c.ses, 0, 0, z, { label, on: c, lanes }));
    if (painted === "stale") stale = true;
    else if (painted === "late") {
      drawShell(ctx, theme, x, y, w, h);
      stale = true;
    } else if (painted === "none") drawSessionBody(ctx, theme, c.ses, x, y, z, { label, on: c, lanes });
    sessionHits(hits, c, x, y, z);
    sessionOverlay(ctx, theme, c, x, y, z, { hover, picked: sel.has(c.key), focus, pid, handling: local?.moved && local.kind === "scale" && local.key === c.key });
    anchors.cards.push({ sid: c.sid, from: c.from, x, y, w, h, color: sevColor(theme, c.ses.met.length ? -1 : c.ses.worst) || theme.lineStrong });
  }

  // Sessions on their way out of a card, or back into it.
  for (const f of flights) {
    const s = scene.sessions.get(f.sid);
    if (!s) continue;
    const e = 1 - (1 - Math.min(1, (now - f.t0) / f.dur)) ** 3;
    ctx.globalAlpha = f.kind === "back" ? 1 - e * 0.6 : 0.4 + 0.6 * e;
    drawSessionBody(ctx, theme, s, lerp(f.from.x, f.to.x, e), lerp(f.from.y, f.to.y, e), lerp(f.from.w, f.to.w, e) / SESSION_W, { label, lifted: true });
    ctx.globalAlpha = 1;
  }

  // Picking several at once.
  if (local?.kind === "marquee" && local.moved) {
    const x0 = Math.min(local.start.x, local.at.x);
    const y0 = Math.min(local.start.y, local.at.y);
    const mw = Math.abs(local.at.x - local.start.x);
    const mh = Math.abs(local.at.y - local.start.y);
    ctx.fillStyle = fade(theme.green, 10);
    ctx.fillRect(x0, y0, mw, mh);
    ctx.strokeStyle = theme.green;
    ctx.lineWidth = 1;
    ctx.strokeRect(x0 + 0.5, y0 + 0.5, mw, mh);
  }

  // What another pane points at, when it is in this pane but out of view: a
  // mark at the pane's edge toward it.
  if (focus && focus.src !== pid) {
    const targets = [];
    for (const b of boxes) {
      const z = k * b.s;
      if (focus.ep && b.kind === "machine" && b.ep === focus.ep) targets.push({ x: SX(b.x), y: SY(b.y), w: b.wu * z, h: b.hu * z });
      if (focus.sid) for (const r of b.rows) if (r.s.id === focus.sid) targets.push({ x: SX(b.x), y: SY(b.y) + r.y * z, w: b.wu * z, h: r.h * z });
    }
    if (focus.sid) for (const c of scene.cards) if (c.sid === focus.sid) targets.push({ x: SX(c.x), y: SY(c.y), w: c.wu * k * c.s, h: c.hu * k * c.s });
    const seen = targets.some((t) => t.x < W && t.y < H && t.x + t.w > 0 && t.y + t.h > 0);
    if (targets.length && !seen) {
      const t = targets[0];
      const cx = clamp(t.x + t.w / 2, 12, W - 12);
      const cy = clamp(t.y + t.h / 2, 12, H - 12);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(Math.atan2(t.y + t.h / 2 - cy, t.x + t.w / 2 - cx));
      ctx.beginPath();
      ctx.moveTo(7, 0);
      ctx.lineTo(-5, -6);
      ctx.lineTo(-5, 6);
      ctx.closePath();
      ctx.fillStyle = theme.green;
      ctx.fill();
      ctx.restore();
    }
  }
  return { hits, anchors, again: again || stale || flights.length > 0, stale };
}

// The hand: what is being carried, drawn over every pane and the page around
// them; the pane it would land in, ringed; and the ties from rows in one pane
// to the sessions pulled out of them into another.
function paintOverlay(cv, deck, theme) {
  const ctx = cv?.getContext?.("2d");
  if (!ctx) return;
  const ties = [];
  for (const [bid, B] of deck.panes) {
    const an = B.anchors();
    const sc = B.scene();
    if (!an?.cards?.length) continue;
    const rb = B.rect();
    if (!rb) continue;
    for (const c of an.cards) {
      if (!c.from || sc?.byKey?.has(c.from)) continue;
      for (const [aid, A] of deck.panes) {
        if (aid === bid) continue;
        const row = A.anchors()?.rows.get(`${c.from}|${c.sid}`);
        const ra = row ? A.rect() : null;
        if (!ra) continue;
        ties.push({ a: { x: ra.left + (row.x0 + row.x1) / 2, x0: ra.left + row.x0, x1: ra.left + row.x1, y: ra.top + row.y }, r: { x: rb.left + c.x, y: rb.top + c.y, w: c.w, h: c.h }, color: c.color });
        break;
      }
    }
  }
  const d = deck.drag;
  const live = d?.live;
  if (!ties.length && !live) {
    if (cv.width) {
      cv.width = 0;
      cv.height = 0;
      cv.style.width = "0px";
      cv.style.height = "0px";
    }
    return;
  }
  const W = window.innerWidth;
  const H = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;
  if (cv.width !== Math.round(W * dpr) || cv.height !== Math.round(H * dpr)) {
    cv.width = Math.round(W * dpr);
    cv.height = Math.round(H * dpr);
    cv.style.width = `${W}px`;
    cv.style.height = `${H}px`;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  for (const t of ties) tie(ctx, t.a, t.r, t.color);
  if (!live) return;
  if (d.target && d.target !== d.src) {
    const r = deck.panes.get(d.target)?.rect();
    if (r) {
      ctx.strokeStyle = theme.green;
      ctx.lineWidth = 2;
      ctx.strokeRect(r.left + 1, r.top + 1, r.width - 2, r.height - 2);
    }
  }
  const dx = d.client.x - d.startClient.x;
  const dy = d.client.y - d.startClient.y;
  if (d.kind === "move") {
    d.items.forEach((it, i) => {
      const x = d.srcRect.left + it.x + dx;
      const y = d.srcRect.top + it.y + dy;
      ctx.globalAlpha = 0.94;
      const sp = sprites.get(`${d.src}|${it.key}`);
      if (i < 6) {
        ctx.save();
        ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
        ctx.shadowBlur = 18;
        ctx.shadowOffsetY = 6;
        ctx.fillStyle = theme.panel;
        roundRect(ctx, x, y, it.w, it.h, 3);
        ctx.fill();
        ctx.restore();
      }
      if (sp) blit(ctx, sp, x, y, it.w, it.h);
      else if (it.o.sid) drawSessionBody(ctx, theme, it.o.ses, x, y, it.zs, { label: machineLabel, lifted: true, on: it.o });
      else drawCardBody(ctx, theme, it.o, x, y, it.zs, { out: new Set(), label: machineLabel });
      ctx.globalAlpha = 1;
    });
  } else if (d.kind === "pull") {
    const zz = lerp(d.rowW / SESSION_W, PULL_W / SESSION_W, d.grow || 0);
    const held = zz / (PULL_W / SESSION_W);
    ctx.globalAlpha = 0.96;
    drawSessionBody(ctx, theme, d.ses, d.client.x - 24 * held, d.client.y - 14 * held, zz, { label: machineLabel, lifted: true });
    ctx.globalAlpha = 1;
  }
}

// What an opener held for a workspace's board when it asked for it in a pane
// (the trace page's record of the session, a session to bring into view).
const handedBoards = new Map();

// The panes' one frame loop: a pane asks to be drawn and is, once, on the
// next frame, with the hand over them all after; a pane gliding, zooming or
// repainting asks again. A hidden page runs no frames, so there a timer
// stands in for one.
function makeDeck() {
  const deck = {
    panes: new Map(),
    dirty: new Set(),
    pending: false,
    gen: 0,
    overlay: null,
    focus: null,
    drag: null,
    active: null,
    lit: null,
    schedule() {
      if (deck.pending) return;
      deck.pending = true;
      const gen = ++deck.gen;
      const run = () => {
        if (!deck.pending || deck.gen !== gen) return;
        deck.pending = false;
        deck.tick();
      };
      raf(run);
      if (typeof document !== "undefined" && document.hidden) setTimeout(run, 32);
    },
    invalidate(id) {
      if (id) deck.dirty.add(id);
      else for (const key of deck.panes.keys()) deck.dirty.add(key);
      deck.schedule();
    },
    // One budget for repainting cards per frame, shared by every pane: the
    // first pane may spend it, and the rest draw what they have and finish
    // on the frames after. Which pane is first goes round, so a pane zooming
    // on and on cannot keep the others from ever finishing.
    turn: 0,
    tick() {
      const all = [...deck.dirty];
      deck.dirty.clear();
      deck.turn = (deck.turn + 1) % Math.max(1, all.length);
      const ids = [...all.slice(deck.turn), ...all.slice(0, deck.turn)];
      const deadline = performance.now() + REPAINT_MS;
      for (const id of ids) {
        const pane = deck.panes.get(id);
        if (pane && pane.draw(deadline)) deck.dirty.add(id);
      }
      deck.overlay?.draw();
      if (deck.dirty.size) deck.schedule();
    },
    // The pane whose canvas is under (x, y): not one under a menu or a
    // name of the page's own lying over it.
    paneAt(x, y) {
      const el = typeof document !== "undefined" ? document.elementFromPoint(x, y) : null;
      if (el && el.tagName !== "CANVAS" && !el.closest?.("[data-fleet-board]")) return null;
      for (const [id, pane] of deck.panes) {
        const r = pane.rect();
        if (r && r.width && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id;
      }
      return null;
    },
    // What is pointed at, lit in every pane.
    setFocus(f) {
      const cur = deck.focus;
      if ((cur?.sid || null) === (f?.sid || null) && (cur?.ep || null) === (f?.ep || null) && (cur?.key || null) === (f?.key || null)) return;
      deck.focus = f;
      deck.invalidate();
    },
  };
  return deck;
}

// What is under the pointer outside the panes while something is carried: a
// buffer's name in a pane's foot, or a workspace in the list beside, lit.
function domTarget(deck, clientX, clientY) {
  const el = document.elementFromPoint(clientX, clientY);
  const buf = el?.closest?.("[data-fleet-buffer]");
  const ws = el?.closest?.("[data-thread-workspace]");
  // A menu held over while carrying something opens, so what is behind it
  // can be dropped on.
  const spring = el?.closest?.("[data-fleet-spring]") || null;
  if (spring !== deck.spring) {
    clearTimeout(deck.springTimer);
    deck.spring = spring;
    if (spring) deck.springTimer = setTimeout(() => spring.dispatchEvent(new CustomEvent("fleet-spring")), 380);
  }
  const lit = buf || ws || null;
  if (deck.lit && deck.lit !== lit) deck.lit.style.removeProperty("box-shadow");
  if (lit && deck.lit !== lit) lit.style.boxShadow = "inset 0 0 0 1px var(--green)";
  deck.lit = lit;
  if (buf) return { type: "buffer", id: buf.getAttribute("data-fleet-buffer") };
  if (ws) return { type: "workspace", id: ws.getAttribute("data-thread-workspace") };
  return null;
}
function clearLit(deck) {
  if (deck.lit) deck.lit.style.removeProperty("box-shadow");
  deck.lit = null;
  clearTimeout(deck.springTimer);
  deck.spring = null;
}
// A menu that opens when something carried is held over it, and closes
// again once that is put down.
function useSpring(ref, setOpen) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let sprung = false;
    const onSpring = () => {
      sprung = true;
      setOpen(true);
    };
    const onUp = () => {
      if (!sprung) return;
      sprung = false;
      setTimeout(() => setOpen(false), 0);
    };
    el.addEventListener("fleet-spring", onSpring);
    window.addEventListener("pointerup", onUp);
    return () => {
      el.removeEventListener("fleet-spring", onSpring);
      window.removeEventListener("pointerup", onUp);
    };
  }, [ref, setOpen]);
}

// -- the panes ----------------------------------------------------------------------

const cell = "flex items-center gap-[6px] whitespace-nowrap border-l border-[color:var(--line-soft)] px-[9px] leading-[16px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";
const cellOn = "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[color:var(--ink)]";
const iconCell = "flex w-[26px] flex-none items-center justify-center border-l border-[color:var(--line-soft)] text-[12px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";
const TIP_TYPES = new Set(["row", "box-head", "frame-head", "stage"]);
const sameHover = (a, b) => (a?.type || null) === (b?.type || null) && (a?.key || null) === (b?.key || null) && (a?.sid || null) === (b?.sid || null);
const cursorOf = (h) => {
  if (!h) return "default";
  if (h.type === "box-corner" || h.type === "card-corner") return "nwse-resize";
  if (h.type === "box-edge") return "ew-resize";
  if (h.type === "box-head" || h.type === "box" || h.type === "card" || h.type === "frame-head") return "grab";
  return h.type === "stage" ? "default" : "pointer";
};

function Working() {
  return (
    <span className="inline-flex shrink-0 items-center gap-[3px]" aria-hidden="true">
      {[0, 160, 320].map((d) => <span key={d} className="inline-block h-[4px] w-[4px] rounded-full bg-[color:var(--muted)] motion-safe:animate-pulse" style={{ animationDelay: `${d}ms` }} />)}
    </span>
  );
}

// A buffer's name in a pane's foot: shown in that pane by a click, renamed by
// a double-click (a buffer of the reader's own), and a place to drop things.
function BufferCell({ b, first, on, count, onPick, onRename, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(b.name);
  const own = b.id !== FLEET && b.id !== FINDINGS && b.id !== TUCKED;
  const done = (keep) => {
    setEditing(false);
    if (keep && text.trim() && text.trim() !== b.name) onRename(text.trim());
    else setText(b.name);
  };
  return (
    <div className={`group relative flex min-w-0 items-stretch ${first ? "" : "border-l border-[color:var(--line-soft)]"} ${on ? cellOn : ""}`} data-fleet-buffer={b.id}>
      {editing ? (
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => done(true)}
          onKeyDown={(e) => { if (e.key === "Enter") done(true); if (e.key === "Escape") done(false); }}
          className="w-[110px] bg-transparent px-[9px] text-[11px] text-[color:var(--ink)] outline-none"
          aria-label="Buffer name"
        />
      ) : (
        <button
          type="button"
          onClick={onPick}
          onDoubleClick={() => { if (own) setEditing(true); }}
          aria-pressed={on}
          className={`flex min-w-0 items-center gap-[6px] whitespace-nowrap px-[9px] leading-[16px] ${on ? "text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
        >
          <span className="min-w-0 truncate">{b.name}</span>
          {count ? <span className="tabular-nums text-[color:var(--muted-soft)]">{count.toLocaleString()}</span> : null}
        </button>
      )}
      {own && !editing ? (
        <button type="button" onClick={onRemove} className="-ml-[5px] hidden w-[16px] items-center justify-center pr-[5px] text-[12px] text-[color:var(--muted)] hover:text-[color:var(--ink)] group-hover:flex" aria-label={`Remove ${b.name}; what is in it goes back where it came from`}>&times;</button>
      ) : null}
    </div>
  );
}

// In a pane too narrow for every buffer's name: the one shown, and a menu of
// them all (each still a place to drop things while it is open).
function BufferMenu({ buffers, buf, counts, onPick, onAdd }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useSpring(ref, setOpen);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    window.addEventListener("pointerdown", away, true);
    return () => window.removeEventListener("pointerdown", away, true);
  }, [open]);
  const all = [...buffers, { id: TUCKED, name: "Tucked away" }];
  const cur = all.find((b) => b.id === buf) || null;
  const item = `${MENU_ITEM} flex w-full items-center justify-between gap-[14px] leading-[16px]`;
  return (
    <div ref={ref} className="relative flex max-w-[140px] flex-none items-stretch" data-fleet-buffer-menu="" data-fleet-spring="">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} className={`flex min-w-0 items-center gap-[6px] whitespace-nowrap px-[9px] leading-[16px] ${cur ? `text-[color:var(--ink)] ${cellOn}` : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}>
        <span className="min-w-0 truncate">{cur ? cur.name : "Buffers"}</span>
        <svg viewBox="0 0 10 10" className="h-[8px] w-[8px] flex-none text-[color:var(--muted)]" aria-hidden="true"><path d="M2 6.5 5 3.5 8 6.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open ? (
        <div role="menu" className={`${MENU} absolute bottom-[calc(100%+6px)] left-0 z-[20] grid min-w-[190px] p-[4px]`}>
          {all.map((b) => (
            <button
              key={b.id}
              type="button"
              role="menuitem"
              data-fleet-buffer={b.id}
              onClick={() => { onPick(b.id); setOpen(false); }}
              className={`${item} ${b.id === buf ? MENU_ITEM_ON : ""}`}
            >
              <span className="min-w-0 truncate">{b.name}</span>
              {counts.get(b.id) ? <span className="tabular-nums text-[color:var(--muted-soft)]">{counts.get(b.id).toLocaleString()}</span> : null}
            </button>
          ))}
          <div className="my-[4px] h-px bg-[color:var(--line-soft)]" />
          <button type="button" role="menuitem" onClick={() => { onAdd(); setOpen(false); }} className={item}>New buffer</button>
        </div>
      ) : null}
    </div>
  );
}

// The workspaces kept in this browser, behind one name: the one the pane
// shows, or "Workspaces". Picking one shows it in the pane; each is a place
// to drop things on while the menu is open, which lays their sessions on it.
function WorkspaceMenu({ workspaces, buf, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useSpring(ref, setOpen);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    window.addEventListener("pointerdown", away, true);
    return () => window.removeEventListener("pointerdown", away, true);
  }, [open]);
  const id = wsOf(buf);
  const cur = id ? workspaces.find((w) => w.id === id) : null;
  const item = `${MENU_ITEM} flex w-full items-center justify-between gap-[14px] leading-[16px]`;
  return (
    <div ref={ref} className={`relative flex min-w-0 items-stretch border-l border-[color:var(--line-soft)] ${id ? cellOn : ""}`} data-fleet-buffer={id ? buf : undefined} data-fleet-workspaces="" data-fleet-spring="">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} className={`flex min-w-0 max-w-[220px] items-center gap-[6px] whitespace-nowrap px-[9px] leading-[16px] ${id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}>
        <span className="min-w-0 truncate">{id ? cur?.title || "Untitled investigation" : "Investigations"}</span>
        {cur ? <span className="tabular-nums text-[color:var(--muted-soft)]">{cur.count}</span> : null}
        <svg viewBox="0 0 10 10" className="h-[8px] w-[8px] flex-none text-[color:var(--muted)]" aria-hidden="true"><path d="M2 6.5 5 3.5 8 6.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open ? (
        <div role="menu" className={`${MENU} absolute bottom-[calc(100%+6px)] left-0 z-[20] grid max-h-[340px] w-[300px] overflow-y-auto p-[4px]`}>
          {workspaces.length ? workspaces.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitem"
              data-fleet-buffer={`${WS}${w.id}`}
              onClick={() => { onPick(`${WS}${w.id}`); setOpen(false); }}
              className={`${item} ${w.id === id ? MENU_ITEM_ON : ""}`}
            >
              <OverflowTooltip text={w.title || "Untitled investigation"} className="min-w-0" />
              <span className="flex-none tabular-nums text-[color:var(--muted-soft)]">{w.count}</span>
            </button>
          )) : (
            <div className="px-[10px] py-[6px] text-[11.5px] leading-[1.45] text-[color:var(--muted)]">No investigations kept in this browser yet. Open a trace&apos;s investigation and it is kept here.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// What a pane says of what is under the pointer.
function TipBody({ tip, scene, model, group }) {
  if (tip.type === "row") {
    const s = model.sessions.get(tip.sid);
    if (!s) return null;
    const { name } = machineLabel(s.endpointId);
    return (
      <div className="grid max-w-[380px] gap-[3px]">
        <div className="text-[12px] font-medium leading-[1.35] text-[color:var(--ink)]">{titleOf(s)}</div>
        <div className="text-[11px] text-[color:var(--muted)]">{[name, agentName(s.agent), whenText(s.firstMs), lengthText((s.lastMs || 0) - (s.firstMs || 0))].filter(Boolean).join(DOT)}</div>
        {s.findings.length ? (
          <div className="text-[11px] text-[color:var(--muted)]">{`${plural(s.findings.length, "finding")}${s.verified ? `${DOT}${s.verified} verified` : ""}${s.met.length ? `${DOT}met ${s.met.join(", ")}` : ""}`}</div>
        ) : null}
        <div className="text-[11px] text-[color:var(--muted-soft)]">{scene.out.has(s.id) ? "Click to put it back." : "Drag it out, into any pane or onto an investigation, or click it, for a card of its own. Double-click to zoom in on the card it is in."}</div>
      </div>
    );
  }
  if (tip.type === "box-head") {
    const b = scene.byKey.get(tip.key);
    if (!b) return null;
    if (b.kind === "outcome") {
      return (
        <div className="grid max-w-[360px] gap-[3px]">
          <div className="text-[12px] font-medium text-[color:var(--ink)]">{b.o.label}</div>
          <div className="text-[11px] text-[color:var(--muted)]">{`${plural(b.o.findings.length, "finding")} in ${plural(b.o.groups.length, "trace")}: ${b.o.why}.`}</div>
          <div className="text-[11px] text-[color:var(--muted-soft)]">Drag it into another pane to keep it beside what it is about, or onto an investigation in the list to lay its traces on there.</div>
        </div>
      );
    }
    const { name, person } = machineLabel(b.ep);
    return (
      <div className="grid gap-[3px]">
        <div className="text-[12px] font-medium text-[color:var(--ink)]">{name}</div>
        <div className="text-[11px] text-[color:var(--muted)]">{[person, plural(b.m.sessions.length, "trace"), b.m.flagged.length ? `${b.m.flagged.length} flagged` : null, b.m.met ? `${b.m.met} met a standard` : null].filter(Boolean).join(DOT)}</div>
        <div className="text-[11px] text-[color:var(--muted-soft)]">Drag it anywhere, into another pane, or onto a buffer below; size it by its corner, or with Option and scroll; widen it by its right edge.</div>
      </div>
    );
  }
  if (tip.type === "frame-head") {
    const f = scene.frames.find((x) => x.key === tip.key);
    if (!f) return null;
    return (
      <div className="grid max-w-[420px] gap-[3px]">
        <div className="text-[12px] font-medium text-[color:var(--ink)]">{f.g.label}</div>
        {f.g.topics?.length ? (
          <div className="text-[11px] text-[color:var(--muted)]">{`What these machines' traces are about: ${f.g.topics.map((t) => `${t.name} ${pct(t.share)}`).join(DOT)}.`}</div>
        ) : null}
        {group === "team" && f.g.people?.length ? <div className="text-[11px] text-[color:var(--muted)]">{f.g.people.map(personName).join(", ")}</div> : null}
        <div className="text-[11px] text-[color:var(--muted-soft)]">{`${group === "work" ? "Machines whose work is alike share a frame, named for the topics that set them apart." : "A frame per department in the directory, a person's machines side by side."} Drag it by its head; drop it in another pane to take its machines there.`}</div>
      </div>
    );
  }
  if (tip.type === "stage") {
    const st = scene.stages.find((s) => s.key === tip.key);
    if (!st) return null;
    const n = st.node;
    return (
      <div className="grid max-w-[340px] gap-[3px]">
        <div className="text-[12px] font-medium text-[color:var(--ink)]">{`${n.label}${DOT}${plural(n.count, "finding")}`}</div>
        <div className="text-[11px] text-[color:var(--muted)]">{STAGE_ABOUT[n.id] || ""}</div>
        {n.share != null ? <div className="text-[11px] text-[color:var(--muted-soft)]">{`${pct(n.share)} of what it came from`}</div> : null}
      </div>
    );
  }
  return null;
}

function Pane({ pane, count, canSplit, deck, api, model, store, theme, themeKey, groupsWork, groupsTeam, flow, outcomes, reading, range, onRangeChange, onFind, workspaces }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const tipRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const buf = pane.buf;
  const bufRef = useRef(buf);
  bufRef.current = buf;
  // A workspace's pane is its board, the trace workspace itself: the one the
  // reader works in is the store's open canvas, for the chat to work on.
  const wsId = wsOf(buf);
  const workspace = useTraceCanvas(wsId);
  const handed = wsId ? handedBoards.get(wsId) || null : null;
  useEffect(() => {
    if (!wsId) return undefined;
    if (!traceCanvasOpen()) showTraceCanvasInDeck(wsId);
    return () => releaseTraceCanvasInDeck(wsId);
  }, [wsId]);
  const shown = useRef(buf);
  const viewRef = useRef(pane.views?.[buf] || { k: 0.8, ox: 24, oy: 24 });
  const needsFit = useRef(!pane.views?.[buf]);
  const autoView = useRef(!!pane.views?.[buf]?.auto);
  const lastZoom = useRef(0);
  const hoverRef = useRef(null);
  const pointRef = useRef({ x: 0, y: 0 });
  const selRef = useRef(new Set());
  const dragRef = useRef(null);
  const glideRef = useRef(new Map());
  const flightsRef = useRef([]);
  const hitsRef = useRef([]);
  const anchorsRef = useRef({ rows: new Map(), cards: [] });
  const viewTimer = useRef(0);
  const settleTimer = useRef(0);
  const [tip, setTip] = useState(null);
  const [note, setNote] = useState("");
  const group = buf === FLEET ? pane.group || "focus" : "concern";
  const groups = buf === FLEET ? (group === "work" ? groupsWork : group === "team" ? groupsTeam : null) : null;
  const ncols = clamp(Math.round((size.w || 900) / 300), 1, 6);
  const aspect = size.w && size.h ? size.w / size.h : 1.8;
  const scene = useMemo(
    () => sceneFor({ model, store, buf, group, groups, flow, outcomes, ncols, aspect }),
    [model, store.boxes, store.cards, store.frames, store.z, buf, group, groups, flow, outcomes, ncols, aspect], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const sceneRef = useRef(scene);
  sceneRef.current = scene;
  const invalidate = () => deck.invalidate(pane.id);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setSize((cur) => (cur.w === el.clientWidth && cur.h === el.clientHeight ? cur : { w: el.clientWidth, h: el.clientHeight }));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Shown another buffer: the view this one was at is kept with it, and the
  // other's taken up where it was left (or the whole of it shown).
  useEffect(() => {
    if (shown.current === buf) return;
    api.setView(pane.id, shown.current, viewRef.current);
    shown.current = buf;
    const v = pane.views?.[buf];
    viewRef.current = v || { k: 0.8, ox: 24, oy: 24 };
    needsFit.current = !v;
    autoView.current = !!v?.auto;
    glideRef.current = new Map();
    selRef.current = new Set();
    hoverRef.current = null;
    setTip(null);
    setNote("");
    invalidate();
  }, [buf]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewChanged = () => {
    clearTimeout(viewTimer.current);
    viewTimer.current = setTimeout(() => api.setView(pane.id, bufRef.current, { ...viewRef.current, auto: autoView.current }), 500);
  };
  const bounds = () => {
    const sc = sceneRef.current;
    const rects = [
      ...sc.frames.map((f) => ({ x0: f.x, y0: f.y, x1: f.x + f.w, y1: f.y + f.h })),
      ...sc.stages.map((s) => ({ x0: s.x, y0: s.y, x1: s.x + s.w, y1: s.y + s.h })),
      ...sc.boxes.map((b) => ({ x0: b.x, y0: b.y, x1: b.x + b.wu * b.s, y1: b.y + b.hu * b.s })),
      ...sc.cards.map((c) => ({ x0: c.x, y0: c.y, x1: c.x + c.wu * c.s, y1: c.y + c.hu * c.s })),
    ];
    if (!rects.length) return null;
    return { x0: Math.min(...rects.map((r) => r.x0)), y0: Math.min(...rects.map((r) => r.y0)), x1: Math.max(...rects.map((r) => r.x1)), y1: Math.max(...rects.map((r) => r.y1)) };
  };
  // Everything in the pane's room.
  const fit = () => {
    const S = sizeRef.current;
    if (!S.w) return;
    const r = bounds();
    if (!r) { viewRef.current = { k: 0.8, ox: 40, oy: 40 }; viewChanged(); return; }
    const k = clamp(Math.min((S.w - 48) / (r.x1 - r.x0), (S.h - 48) / (r.y1 - r.y0)), 0.03, 1.2);
    viewRef.current = { k, ox: (S.w - (r.x1 - r.x0) * k) / 2 - r.x0 * k, oy: Math.max(24, (S.h - (r.y1 - r.y0) * k) / 2) - r.y0 * k };
    viewChanged();
  };
  // As wide as the room: what most needs a look, readable. From the top, or
  // (keeping its place) from where it was scrolled to.
  const fitWidth = (keepTop = false) => {
    const S = sizeRef.current;
    if (!S.w) return;
    const r = bounds();
    if (!r) { viewRef.current = { k: 0.8, ox: 40, oy: 40 }; viewChanged(); return; }
    const k = clamp((S.w - 48) / (r.x1 - r.x0), 0.3, 1.3);
    const v = viewRef.current;
    const top = keepTop ? Math.max(r.y0 - 30 / k, (0 - v.oy) / v.k) : r.y0 - 30 / k;
    viewRef.current = { k, ox: (S.w - (r.x1 - r.x0) * k) / 2 - r.x0 * k, oy: -top * k };
    viewChanged();
  };
  // Look now opens with what leads and what places it across the pane, from
  // the top of its frame: the context beside it in view, the rest below.
  const fitLook = () => {
    const S = sizeRef.current;
    const f = sceneRef.current.frames.find((x) => x.key === "g:look");
    const r = bounds();
    if (!S.w || !f || !r) return fit();
    const k = clamp((S.w - 48) / (r.x1 - r.x0), 0.05, 1.1);
    viewRef.current = { k, ox: (S.w - (r.x1 - r.x0) * k) / 2 - r.x0 * k, oy: 24 - (f.y - 12) * k };
    viewChanged();
  };
  const autoFit = (keepTop = false) => (buf === FLEET && group === "focus" ? fitLook()
    : (buf === FLEET && !groups) || buf === FINDINGS ? fitWidth(keepTop) : fit());
  // A buffer opens where it was left, or (once what it shows is in) with what
  // it holds in the room; the view it chose follows the room as it settles,
  // until the reader moves it.
  const ready = buf === FINDINGS ? !!flow : buf === FLEET && group === "focus" ? model.findingsReady : model.settled;
  useEffect(() => {
    if (!needsFit.current || !size.w || !ready) return;
    needsFit.current = false;
    autoView.current = true;
    autoFit();
    invalidate();
  }, [scene, size.w, ready]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!autoView.current || needsFit.current || !size.w || !ready) return;
    autoFit(true);
    invalidate();
  }, [size.w, size.h, groups, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // The view on its way to another: eased over a quarter second, the point at
  // the pane's centre moving straight and the scale evenly, so a zoom reads
  // as one move. Anything the reader does to the view stops it.
  const tweenRef = useRef(null);
  const glideViewTo = (to) => {
    tweenRef.current = { from: { ...viewRef.current }, to, t0: performance.now(), dur: 280 };
    autoView.current = false;
    invalidate();
  };
  const zoomTo = (o) => {
    const S = sizeRef.current;
    if (!o || !S.w) return;
    const w = (o.wu || CARD_U) * o.s;
    const h = o.hu * o.s;
    const k = clamp(Math.min((S.w - 56) / w, (S.h - 56) / h), 0.05, 3);
    glideViewTo({ k, ox: (S.w - w * k) / 2 - o.x * k, oy: (S.h - h * k) / 2 - o.y * k });
  };

  // The pane's frame, drawn when the deck asks for it.
  const drawRef = useRef(null);
  drawRef.current = (deadline) => {
    const cv = canvasRef.current;
    const ctx = cv?.getContext?.("2d");
    const S = sizeRef.current;
    if (!ctx || !S.w || !S.h) return false;
    const dpr = window.devicePixelRatio || 1;
    const bw = Math.round(S.w * dpr);
    const bh = Math.round(S.h * dpr);
    if (cv.width !== bw || cv.height !== bh) {
      cv.width = bw;
      cv.height = bh;
    }
    const now = performance.now();
    flightsRef.current = flightsRef.current.filter((f) => now - f.t0 < f.dur);
    const tw = tweenRef.current;
    if (tw) {
      const e = Math.min(1, (now - tw.t0) / tw.dur);
      const f = 1 - (1 - e) ** 3;
      const k = Math.exp(lerp(Math.log(tw.from.k), Math.log(tw.to.k), f));
      const cx = lerp((S.w / 2 - tw.from.ox) / tw.from.k, (S.w / 2 - tw.to.ox) / tw.to.k, f);
      const cy = lerp((S.h / 2 - tw.from.oy) / tw.from.k, (S.h / 2 - tw.to.oy) / tw.to.k, f);
      viewRef.current = { k, ox: S.w / 2 - cx * k, oy: S.h / 2 - cy * k };
      lastZoom.current = now;
      if (e >= 1) {
        tweenRef.current = null;
        viewRef.current = { ...tw.to };
        viewChanged();
      }
    }
    const D = deck.drag;
    const lifted = D?.live && D.kind === "move" ? D.lifted : null;
    const pulling = D?.live && D.kind === "pull" ? D.sid : null;
    const sc = sceneRef.current;
    const res = paintPane(ctx, {
      W: S.w, H: S.h, dpr, view: viewRef.current, theme, scene: sc, label: machineLabel, pid: pane.id,
      revBase: `${model.rev}|${model.labelsTick}|${themeKey}`,
      out: pulling ? new Set([...sc.out, pulling]) : sc.out,
      hover: hoverRef.current, sel: selRef.current, lifted, focus: deck.focus, flights: flightsRef.current, now,
      zooming: now - lastZoom.current < ZOOM_SETTLE_MS, glide: glideRef.current, local: dragRef.current, mode: group, deadline,
    });
    hitsRef.current = res.hits;
    anchorsRef.current = res.anchors;
    return res.again || !!tweenRef.current;
  };
  const keyRef = useRef(null);
  useEffect(() => {
    deck.panes.set(pane.id, {
      draw: (deadline) => drawRef.current(deadline),
      rect: () => (canvasRef.current || wrapRef.current)?.getBoundingClientRect() || null,
      board: () => !!wsOf(bufRef.current),
      view: () => viewRef.current,
      buf: () => bufRef.current,
      scene: () => sceneRef.current,
      anchors: () => anchorsRef.current,
      key: (e) => keyRef.current?.(e),
    });
    deck.invalidate(pane.id);
    return () => {
      deck.panes.delete(pane.id);
      clearTimeout(viewTimer.current);
      clearTimeout(settleTimer.current);
    };
  }, [deck, pane.id]);
  useEffect(() => { invalidate(); }, [scene, theme, size, themeKey, model.labelsTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const hitAt = (x, y) => {
    const hits = hitsRef.current;
    for (let i = hits.length - 1; i >= 0; i -= 1) {
      const h = hits[i];
      if (x >= h.x0 && x <= h.x1 && y >= h.y0 && y <= h.y1) return h;
    }
    return null;
  };
  const local = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const objectOf = (key) => {
    const sc = sceneRef.current;
    if (!key) return null;
    if (key.startsWith("c:") || key.startsWith("h:")) return sc.cards.find((c) => c.key === key) || null;
    if (key.startsWith("g:")) return sc.frames.find((f) => f.key === key) || null;
    return sc.byKey.get(key) || null;
  };
  // Where something is on screen in this pane now (gliding, or not).
  const screenOf = (o) => {
    const v = viewRef.current;
    const g = glideRef.current.get(o.key) || o;
    return { x: g.x * v.k + v.ox, y: g.y * v.k + v.oy };
  };
  // Sizes one card about a point on it, keeping that point under the
  // pointer; a card in the flow keeps its place there instead.
  const scaleAt = (key, f, p) => {
    const o = objectOf(key);
    if (!o || o.kind === "frame" || o.kind === "stage") return;
    const v = viewRef.current;
    const w = { x: (p.x - v.ox) / v.k, y: (p.y - v.oy) / v.k };
    const s1 = clamp(o.s * f, 0.15, 6);
    const r = s1 / o.s;
    const patch = { s: s1, x: w.x - (w.x - o.x) * r, y: w.y - (w.y - o.y) * r };
    if (o.sid) api.patchCard(o.sid, patch, false);
    else if (!o.placed) api.patchBox(o.id, { s: s1 }, false);
    else api.patchBox(o.id, { ...(o.kind === "machine" ? { buf: bufRef.current } : {}), ...patch }, false);
  };
  // A session out of its card beside its row, on whichever side the pane has
  // room for it, the size a pulled one grows to.
  const pullOut = (sid, b) => {
    const ses = model.sessions.get(sid);
    if (!ses || !b) return;
    const v = viewRef.current;
    const S = sizeRef.current;
    const g = glideRef.current.get(b.key);
    const a = rowAnchor(g ? { ...b, ...g } : b, sid, v.k, v.ox, v.oy);
    const zc = PULL_W / SESSION_W;
    const right = a.x1 + 16;
    const sx = right + PULL_W < S.w - 12 ? right : Math.max(12, a.x0 - 16 - PULL_W);
    const sy = clamp(a.rowY - 8, 12, Math.max(12, S.h - sessionUnits(ses) * zc - 12));
    if (!document.hidden) flightsRef.current.push({ sid, kind: "out", from: { x: a.x0, y: a.rowY, w: a.x1 - a.x0 }, to: { x: sx, y: sy, w: PULL_W }, t0: performance.now(), dur: 260 });
    api.commit(placeItem(api.store(), { kind: "session", sid, from: b.key }, bufRef.current, (sx - v.ox) / v.k, (sy - v.oy) / v.k, zc / v.k));
    api.touch(`c:${sid}`);
    invalidate();
  };
  const putBack = (sid) => {
    const s = api.store();
    const at = s.cards[sid];
    if (!at) return;
    const sc = sceneRef.current;
    const b = (at.from && sc.byKey.get(at.from)) || sc.rowBox.get(sid) || sc.boxOf.get(model.sessions.get(sid)?.endpointId);
    const v = viewRef.current;
    if (b && (at.buf || FLEET) === bufRef.current && !document.hidden) {
      const g = glideRef.current.get(b.key);
      const a = rowAnchor(g ? { ...b, ...g } : b, sid, v.k, v.ox, v.oy);
      flightsRef.current.push({ sid, kind: "back", from: { x: at.x * v.k + v.ox, y: at.y * v.k + v.oy, w: SESSION_W * v.k * (at.s || 1) }, to: { x: a.x0, y: a.rowY, w: a.x1 - a.x0 }, t0: performance.now(), dur: 260 });
    }
    const { [sid]: _gone, ...cards } = s.cards;
    api.commit({ ...s, cards });
    invalidate();
  };
  const selected = () => [...selRef.current].map(objectOf).filter((o) => o && (o.sid || o.kind === "machine" || o.kind === "outcome"));
  const tuckSelected = () => {
    const items = selected().map((o) => itemOf(o, viewRef.current.k));
    if (!items.length) return;
    setNote(bufRef.current === TUCKED ? api.untuck(items) : api.send(items, TUCKED));
    selRef.current = new Set();
    invalidate();
  };
  keyRef.current = (e) => {
    if (e.key === "f" || e.key === "F") {
      // Back to the view the pane would choose: the Fleet's flow and the
      // Findings as wide as the pane, anything else all of it in view.
      autoView.current = true;
      autoFit();
      invalidate();
    } else if (e.key === "Escape") {
      selRef.current = new Set();
      invalidate();
    } else if ((e.key === "Backspace" || e.key === "Delete") && selRef.current.size) {
      e.preventDefault();
      tuckSelected();
    }
  };

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      deck.active = pane.id;
      tweenRef.current = null;
      const r = cv.getBoundingClientRect();
      const p = { x: e.clientX - r.left, y: e.clientY - r.top };
      if (e.altKey) {
        // Option and scroll sizes the card under the pointer.
        const h = hitAt(p.x, p.y);
        if (h?.key && /^[bco]:/.test(h.key)) {
          api.gestureBegin();
          api.touch(h.key);
          scaleAt(h.key, Math.exp(-e.deltaY * 0.0022), p);
          clearTimeout(settleTimer.current);
          settleTimer.current = setTimeout(() => api.gestureEnd(), 300);
        }
        return;
      }
      const v = viewRef.current;
      if (e.ctrlKey || e.metaKey) autoView.current = false;
      if (e.ctrlKey || e.metaKey) {
        const k = clamp(v.k * Math.exp(-e.deltaY * 0.0022), 0.03, 3);
        viewRef.current = { k, ox: p.x - ((p.x - v.ox) / v.k) * k, oy: p.y - ((p.y - v.oy) / v.k) * k };
        lastZoom.current = performance.now();
      } else {
        viewRef.current = { ...v, ox: v.ox - e.deltaX, oy: v.oy - e.deltaY };
      }
      viewChanged();
      invalidate();
    };
    cv.addEventListener("wheel", onWheel, { passive: false });
    return () => cv.removeEventListener("wheel", onWheel);
  }, [!!wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  // What the pointer is over, said once when it changes: the tooltip, the
  // cursor, and what every pane lights.
  const point = (h, p) => {
    pointRef.current = p;
    if (tipRef.current) {
      const S = sizeRef.current;
      tipRef.current.style.left = `${Math.min(p.x + 14, S.w - 400)}px`;
      tipRef.current.style.top = `${Math.min(p.y + 16, S.h - 110)}px`;
    }
    const next = h ? { type: h.type, key: h.key, sid: h.sid } : null;
    if (sameHover(hoverRef.current, next)) return;
    hoverRef.current = next;
    if (canvasRef.current) canvasRef.current.style.cursor = cursorOf(next);
    setTip(next && TIP_TYPES.has(next.type) ? next : null);
    const sc = sceneRef.current;
    let focus = null;
    if (next?.sid) focus = { sid: next.sid, ep: model.sessions.get(next.sid)?.endpointId || null, key: next.key, src: pane.id };
    else if (next && (next.type === "box-head" || next.type === "box")) {
      const b = sc.byKey.get(next.key);
      if (b?.kind === "machine") focus = { ep: b.ep, key: b.key, src: pane.id };
    }
    deck.setFocus(focus);
    invalidate();
  };
  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const p = pointRef.current;
    const S = sizeRef.current;
    tipRef.current.style.left = `${Math.min(p.x + 14, S.w - 400)}px`;
    tipRef.current.style.top = `${Math.min(p.y + 16, S.h - 110)}px`;
  }, [tip]);

  // Picking up: everything picked, if what is pressed is picked, else just
  // it. A card in the flow comes out of it where it is, the flow closing up
  // behind it.
  const liftItems = (d, e) => {
    const sel = selRef.current;
    const keys = sel.has(d.key) && sel.size > 1 ? [...sel] : [d.key];
    if (!sel.has(d.key)) selRef.current = new Set([d.key]);
    const objs = keys.map(objectOf).filter((o) => o && (o.sid || o.kind === "machine" || o.kind === "outcome"));
    if (!objs.length) return false;
    api.gestureBegin();
    let s = api.store();
    const v = viewRef.current;
    const items = objs.map((o) => {
      const at = screenOf(o);
      const it = itemOf(o, v.k);
      if (!o.sid && !o.placed) s = { ...s, boxes: { ...s.boxes, [o.id]: { ...(s.boxes[o.id] || {}), ...(o.kind === "machine" ? { buf: bufRef.current } : {}), x: o.x, y: o.y } } };
      return { ...it, o, x: at.x, y: at.y, w: o.wu * it.zs, h: o.hu * it.zs };
    });
    if (s !== api.store()) api.put(s, false);
    deck.drag = {
      kind: "move", live: true, src: pane.id, items, lifted: new Set(items.map((it) => it.key)),
      srcRect: canvasRef.current.getBoundingClientRect(), startClient: { x: d.client.x, y: d.client.y }, client: { x: e.clientX, y: e.clientY }, target: pane.id,
    };
    return true;
  };
  const begin = (d) => {
    dragRef.current = d;
  };
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture is an enhancement */ }
    deck.active = pane.id;
    tweenRef.current = null;
    const p = local(e);
    const h = hitAt(p.x, p.y);
    const v = viewRef.current;
    const client = { x: e.clientX, y: e.clientY };
    setNote("");
    if (!h || h.type === "stage") {
      begin(e.shiftKey ? { kind: "marquee", start: p, at: p, moved: false } : { kind: "pan", start: p, view: v, shift: e.shiftKey, moved: false });
      return;
    }
    const o = objectOf(h.key);
    if (h.key && o) api.touch(h.key);
    if ((h.type === "box-corner" || h.type === "card-corner") && o) {
      const at = screenOf(o);
      begin({ kind: "scale", key: h.key, start: p, s0: o.s, sx: at.x, sy: at.y, w0: (o.wu || SESSION_W) * o.s * v.k, h0: o.hu * o.s * v.k, moved: false });
    } else if (h.type === "box-edge" && o) {
      begin({ kind: "width", key: h.key, start: p, moved: false });
    } else if (h.type === "row" && o) {
      begin({ kind: "pull", key: h.key, sid: h.sid, start: p, client, rowW: o.wu * o.s * v.k, moved: false });
    } else if (h.type === "frame-head" && o) {
      const w = { x: (p.x - v.ox) / v.k, y: (p.y - v.oy) / v.k };
      begin({ kind: "frame", key: h.key, start: p, client, grab: { x: w.x - o.x, y: w.y - o.y }, from: { x: o.x, y: o.y }, moved: false });
    } else if (h.type === "card" && o?.hero) {
      begin({ kind: "pan", start: p, view: v, shift: e.shiftKey, moved: false });
    } else if ((h.type === "box" || h.type === "box-head" || h.type === "card") && o) {
      begin({ kind: "move", key: h.key, start: p, client, shift: e.shiftKey, moved: false });
    } else {
      begin({ kind: "act", hit: h, start: p, moved: false });
    }
  };
  const onPointerMove = (e) => {
    const p = local(e);
    const d = dragRef.current;
    if (!d) {
      point(hitAt(p.x, p.y), p);
      return;
    }
    if (!d.moved && Math.abs(p.x - d.start.x) + Math.abs(p.y - d.start.y) > 4) {
      d.moved = true;
      if (tip) setTip(null);
      if (d.kind === "move" && !liftItems(d, e)) d.kind = "pan";
      if (d.kind === "pull") {
        const ses = model.sessions.get(d.sid);
        if (!ses) d.kind = "pan";
        else deck.drag = { kind: "pull", live: true, src: pane.id, sid: d.sid, ses, fromKey: d.key, rowW: d.rowW, startClient: d.client, client: { x: e.clientX, y: e.clientY }, target: pane.id, grow: 0 };
      }
      if (d.kind === "scale" || d.kind === "width" || d.kind === "frame") api.gestureBegin();
      if (d.kind === "frame") deck.drag = { kind: "frame", live: true, src: pane.id, client: { x: e.clientX, y: e.clientY }, startClient: d.client, target: pane.id };
      if (d.kind === "pan") d.view = viewRef.current;
      if (canvasRef.current) canvasRef.current.style.cursor = d.kind === "scale" ? "nwse-resize" : d.kind === "width" ? "ew-resize" : d.kind === "marquee" ? "crosshair" : "grabbing";
      deck.invalidate();
    }
    if (!d.moved || d.kind === "act") return;
    const v = viewRef.current;
    const w = { x: (p.x - v.ox) / v.k, y: (p.y - v.oy) / v.k };
    const o = d.key ? objectOf(d.key) : null;
    if (d.kind === "pan") {
      if (Math.abs(p.x - d.start.x) > 24) autoView.current = false;
      viewRef.current = { ...d.view, ox: d.view.ox + p.x - d.start.x, oy: d.view.oy + p.y - d.start.y };
      viewChanged();
      invalidate();
    } else if (d.kind === "marquee") {
      d.at = p;
      invalidate();
    } else if (d.kind === "frame" && o) {
      api.patchFrame(o.gid, { x: w.x - d.grab.x, y: w.y - d.grab.y }, false);
      deck.drag.client = { x: e.clientX, y: e.clientY };
      deck.drag.target = deck.paneAt(e.clientX, e.clientY);
      deck.schedule();
    } else if (d.kind === "scale" && o) {
      const f = Math.max(0.05, ((p.x - d.sx) + (p.y - d.sy)) / (d.w0 + d.h0));
      const s1 = clamp(d.s0 * f, 0.15, 6);
      if (o.sid) api.patchCard(o.sid, { s: s1 }, false);
      else api.patchBox(o.id, o.placed ? { ...(o.kind === "machine" ? { buf: bufRef.current } : {}), x: o.x, y: o.y, s: s1 } : { s: s1 }, false);
    } else if (d.kind === "width" && o && !o.sid) {
      const wu = clamp((w.x - o.x) / o.s, CARD_MIN, CARD_MAX);
      api.patchBox(o.id, o.placed ? { ...(o.kind === "machine" ? { buf: bufRef.current } : {}), x: o.x, y: o.y, w: wu } : { w: wu }, false);
    } else if ((d.kind === "move" || d.kind === "pull") && deck.drag) {
      const D = deck.drag;
      D.client = { x: e.clientX, y: e.clientY };
      D.target = deck.paneAt(e.clientX, e.clientY);
      if (!D.target) domTarget(deck, e.clientX, e.clientY);
      else clearLit(deck);
      if (d.kind === "pull" && o) {
        // It grows from its row into a card as it leaves the card it was in.
        const at = screenOf(o);
        const bx1 = at.x + o.wu * o.s * v.k;
        const by1 = at.y + o.hu * o.s * v.k;
        const dx = D.target === pane.id ? Math.max(at.x - p.x, 0, p.x - bx1) : 120;
        const dy = D.target === pane.id ? Math.max(at.y - p.y, 0, p.y - by1) : 0;
        D.grow = smooth(0, 120, Math.hypot(dx, dy) + Math.hypot(p.x - d.start.x, p.y - d.start.y) * 0.35);
      }
      deck.schedule();
    }
  };
  // Where a carried thing lands: in the pane under the pointer (this one or
  // another) at the size it was on screen, on a buffer's name, on a workspace
  // in the list, or (dropped on nothing) back where it was.
  const dropMove = (e) => {
    const D = deck.drag;
    const tgt = deck.paneAt(e.clientX, e.clientY);
    const dom = tgt ? null : domTarget(deck, e.clientX, e.clientY);
    const dx = e.clientX - D.startClient.x;
    const dy = e.clientY - D.startClient.y;
    if (tgt) {
      const T = deck.panes.get(tgt);
      const vT = T.view();
      const rT = T.rect();
      const tbuf = T.buf();
      // Onto a workspace's board, what is carried is laid on: a session, or
      // what in a machine or an outcome most needs a look (those stay where
      // they are); the board puts each where its time puts it.
      const tws = wsOf(tbuf);
      let s = api.store();
      const on = [];
      for (const it of D.items) {
        const px = D.srcRect.left + it.x + dx - rT.left;
        const py = D.srcRect.top + it.y + dy - rT.top;
        if (!tws) s = placeItem(s, it, tbuf, (px - vT.ox) / vT.k, (py - vT.oy) / vT.k, it.zs / vT.k);
        else if (it.kind === "session") {
          if (s.cards[it.sid]) {
            const { [it.sid]: _moved, ...cards } = s.cards;
            s = { ...s, cards };
          }
          on.push(it.sid);
        } else on.push(...api.sessionsOf(it.o).filter((sid) => !on.includes(sid)));
      }
      const ws = tws ? api.wsChange([[tws, on, []]]) : null;
      api.gestureEnd(s, ws);
      if (tgt !== pane.id) {
        const what = D.items.length === 1 ? api.nameOf(D.items[0]) : plural(D.items.length, "thing");
        setNote(tws ? api.wsSaid(tws, ws, on.length) : `${what} moved to ${api.bufferName(tbuf)}`);
        selRef.current = new Set();
      }
      deck.invalidate(tgt);
    } else if (dom?.type === "buffer") {
      api.gestureRevert();
      setNote(dom.id === bufRef.current ? "" : api.send(D.items, dom.id));
      selRef.current = new Set();
    } else if (dom?.type === "workspace") {
      api.gestureRevert();
      setNote(api.layOn(dom.id, D.items.flatMap((it) => api.sessionsOf(it.o))));
    } else {
      api.gestureRevert();
    }
  };
  const dropPull = (e) => {
    const D = deck.drag;
    const tgt = deck.paneAt(e.clientX, e.clientY);
    const dom = tgt ? null : domTarget(deck, e.clientX, e.clientY);
    if (tgt) {
      const T = deck.panes.get(tgt);
      const vT = T.view();
      const rT = T.rect();
      const zz = lerp(D.rowW / SESSION_W, PULL_W / SESSION_W, D.grow || 0);
      const held = zz / (PULL_W / SESSION_W);
      const px = e.clientX - 24 * held - rT.left;
      const py = e.clientY - 14 * held - rT.top;
      const o = objectOf(D.fromKey);
      if (tgt === pane.id && o && (D.grow || 0) < 0.5) {
        const at = screenOf(o);
        const p = local(e);
        if (p.x >= at.x && p.x <= at.x + o.wu * o.s * viewRef.current.k && p.y >= at.y && p.y <= at.y + o.hu * o.s * viewRef.current.k) return;
      }
      const tws = wsOf(T.buf());
      if (tws) {
        const ws = api.wsChange([[tws, [D.sid], []]]);
        api.commit(api.store(), ws);
        setNote(api.wsSaid(tws, ws, 1));
      } else {
        api.commit(placeItem(api.store(), { kind: "session", sid: D.sid, from: D.fromKey }, T.buf(), (px - vT.ox) / vT.k, (py - vT.oy) / vT.k, zz / vT.k));
        if (tgt !== pane.id) setNote(`"${short(titleOf(D.ses), 48)}" is in ${api.bufferName(T.buf())} now, tied to its row here`);
      }
      api.touch(`c:${D.sid}`);
      deck.invalidate(tgt);
    } else if (dom?.type === "buffer") {
      setNote(api.send([{ kind: "session", sid: D.sid, s: (PULL_W / SESSION_W) / viewRef.current.k, zs: PULL_W / SESSION_W, from: D.fromKey }], dom.id));
    } else if (dom?.type === "workspace") {
      setNote(api.layOn(dom.id, [D.sid]));
    }
  };
  // A frame dropped in another pane takes its machines there, laid out as
  // they were in it; the frame stays where it was.
  const dropFrame = (e, d) => {
    const tgt = deck.paneAt(e.clientX, e.clientY);
    const f = objectOf(d.key);
    if (!tgt || tgt === pane.id || !f) {
      api.gestureEnd();
      return;
    }
    const T = deck.panes.get(tgt);
    const vT = T.view();
    const rT = T.rect();
    const rS = canvasRef.current.getBoundingClientRect();
    const v = viewRef.current;
    let s = api.store();
    const members = sceneRef.current.boxes.filter((b) => b.frame === f.key);
    for (const b of members) {
      const at = screenOf(b);
      const px = rS.left + at.x - rT.left;
      const py = rS.top + at.y - rT.top;
      s = placeItem(s, itemOf(b, v.k), T.buf(), (px - vT.ox) / vT.k, (py - vT.oy) / vT.k, (b.s * v.k) / vT.k);
    }
    const mode = group;
    const kept = s.frames?.[mode] || {};
    s = { ...s, frames: { ...s.frames, [mode]: { ...kept, [f.gid]: { ...d.from } } } };
    api.gestureEnd(s);
    setNote(`${plural(members.length, "machine")} of ${f.g.label} moved to ${api.bufferName(T.buf())}`);
    deck.invalidate(tgt);
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    const p = local(e);
    if (!d.moved) {
      if (d.kind === "pull") {
        if (sceneRef.current.out.has(d.sid)) putBack(d.sid);
        else pullOut(d.sid, objectOf(d.key));
      } else if (d.kind === "move") {
        const sel = new Set(d.shift ? selRef.current : []);
        if (d.shift && sel.has(d.key)) sel.delete(d.key);
        else sel.add(d.key);
        selRef.current = sel;
      } else if (d.kind === "pan" && !d.shift) {
        selRef.current = new Set();
      } else if (d.kind === "act") {
        const sid = d.hit.key?.slice(2);
        if (d.hit.type === "card-close") putBack(sid);
        else if (d.hit.type === "card-zoom") zoomTo(objectOf(d.hit.key));
        else if (d.hit.type === "card-board") {
          const id = keepTraceCanvas({ id: sid, title: objectOf(d.hit.key)?.ses?.title || "" });
          if (id) api.showWorkspace(id, pane.id);
        }
        else if (d.hit.type === "foot") {
          const o = objectOf(d.hit.key);
          if (o) api.commit(placeOpen(api.store(), o, bufRef.current));
        }
      }
      invalidate();
      point(hitAt(p.x, p.y), p);
      return;
    }
    if (d.kind === "marquee") {
      const v = viewRef.current;
      const x0 = Math.min(d.start.x, d.at.x);
      const y0 = Math.min(d.start.y, d.at.y);
      const x1 = Math.max(d.start.x, d.at.x);
      const y1 = Math.max(d.start.y, d.at.y);
      const sel = new Set(selRef.current);
      const sc = sceneRef.current;
      for (const o of [...sc.boxes, ...sc.cards]) {
        const at = screenOf(o);
        const w = o.wu * o.s * v.k;
        const h = o.hu * o.s * v.k;
        if (at.x < x1 && at.x + w > x0 && at.y < y1 && at.y + h > y0) sel.add(o.key);
      }
      selRef.current = sel;
    } else if (d.kind === "frame") {
      dropFrame(e, d);
    } else if (d.kind === "scale" || d.kind === "width") {
      api.gestureEnd();
    } else if (d.kind === "move" && deck.drag?.kind === "move") {
      dropMove(e);
    } else if (d.kind === "pull" && deck.drag?.kind === "pull") {
      dropPull(e);
    }
    deck.drag = null;
    clearLit(deck);
    hoverRef.current = null;
    deck.invalidate();
    point(hitAt(p.x, p.y), p);
  };
  // Double-clicked, a card fills the pane: a session's card, or the card a
  // row is in.
  const onDoubleClick = (e) => {
    const p = local(e);
    const h = hitAt(p.x, p.y);
    if (h?.key && (h.type === "row" || h.type === "card" || h.type === "box-head")) zoomTo(objectOf(h.key));
  };
  const onPointerLeave = () => {
    if (dragRef.current) return;
    hoverRef.current = null;
    setTip(null);
    if (deck.focus?.src === pane.id) deck.setFocus(null);
    invalidate();
  };

  const bufName = api.bufferName(buf);
  const here = scene.boxes.length + scene.cards.length + scene.stages.length;
  const flagged = model.machines.reduce((n, m) => n + m.flagged.length, 0);
  const met = model.machines.reduce((n, m) => n + m.met, 0);
  const paneReading = [
    ...model.reading,
    ...(group === "work" && reading.work ? ["Reading what the machines work on"] : []),
    ...(group === "team" && reading.people ? ["Reading who each machine belongs to"] : []),
    ...(buf === FINDINGS && reading.sums ? ["Reading what the reviews wrote"] : []),
  ];
  const findingsSaid = () => {
    const kept = flow?.nodes.get("kept")?.count || model.findings.length;
    const triaged = flow?.nodes.get("triaged")?.count || 0;
    const metN = outcomes.find((o) => o.id === "met")?.findings.length || 0;
    return [`${kept.toLocaleString()} findings kept`, `${triaged.toLocaleString()} read by Triage`, metN ? `${metN} met a standard` : null, model.clipped ? `the newest ${model.findings.length.toLocaleString()} read` : null].filter(Boolean).join(DOT);
  };
  const looked = () => {
    const by = new Map(outcomes.map((o) => [o.id, o]));
    const metO = by.get("met");
    return [
      metO ? `${plural(metO.findings.length, "finding")} met a standard in ${plural(metO.groups.length, "trace")}` : "nothing met a standard",
      by.get("not_met") ? `${num(by.get("not_met").findings.length)} true, no standard met` : null,
      by.get("not_triaged") ? `${num(by.get("not_triaged").findings.length)} not verified yet` : null,
    ].filter(Boolean).join(DOT);
  };
  const status = paneReading.length ? paneReading.join(DOT)
    : note || (buf === FLEET && group === "focus" ? looked() : buf === FLEET
      ? [groups ? plural(groups.length, "group") : null, plural(scene.boxes.filter((b) => b.kind === "machine").length, "machine"), `${plural(model.sessions.size, "trace")}${model.capped ? " (the newest, and every one flagged)" : ""}`, flagged ? `${flagged.toLocaleString()} flagged` : null, met ? `${met.toLocaleString()} met a standard` : null].filter(Boolean).join(DOT)
      : buf === FINDINGS ? findingsSaid()
        : buf === TUCKED ? "Out of sight. Drag any of it into another pane to bring it back, or pick it and press Delete."
          : wsId ? (workspace.anchor ? [`${plural(1 + workspace.layered.length, "trace")} on one clock`, "drag a trace or a machine in to lay it on"].join(DOT) : "")
            : [plural(scene.boxes.length, "card"), plural(scene.cards.length, "trace")].join(DOT));
  const counts = api.counts();
  // The buffers' names side by side where the foot has room for them, after
  // the grouping and the pane's own controls; behind one name where not.
  const tabsW = store.buffers.reduce((n, b) => n + 20 + b.name.length * 6.6 + (counts.get(b.id) ? 16 : 0), 0) + 26 + 92 + 130 + (wsId ? 50 : 0);
  const compact = size.w > 0 && size.w < tabsW + (buf === FLEET ? 170 : 0) + 230 + 140;

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)]" aria-label={bufName} data-fleet-pane={pane.id}>
      {wsId ? (
        // The workspace itself: its board, everything it does, worked in
        // here. Pointing into it makes it the workspace the chat works on; a
        // session dragged from the list onto it is laid on.
        <div
          ref={wrapRef}
          className="relative min-h-0 min-w-0 flex-1"
          data-fleet-board=""
          onPointerDownCapture={() => {
            deck.active = pane.id;
            showTraceCanvasInDeck(wsId);
          }}
          onDragOver={(e) => {
            if (![...(e.dataTransfer?.types || [])].includes(SESSION_DRAG_TYPE)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDrop={(e) => {
            if (![...(e.dataTransfer?.types || [])].includes(SESSION_DRAG_TYPE)) return;
            e.preventDefault();
            // Laid on here: the page around it only lets go of its cue.
            e.nativeEvent.laidOn = true;
            try {
              const entry = JSON.parse(e.dataTransfer.getData(SESSION_DRAG_TYPE) || "null");
              if (entry?.id) setNote(api.wsSaid(wsId, api.wsChange([[wsId, [entry.id], []]]), 1));
            } catch { /* not a session */ }
          }}
        >
          {workspace.anchor ? (
            <TraceCanvas
              key={wsId}
              canvasId={wsId}
              trace={handed?.trace || null}
              focus={handed?.focus || null}
              range={range}
              onRangeChange={onRangeChange}
              onAddRequest={onFind}
            />
          ) : (
            <div className="grid h-full place-items-center px-[24px] text-center text-[12px] text-[color:var(--muted)]">This investigation is no longer kept in this browser. Pick another from Investigations below.</div>
          )}
        </div>
      ) : (
      <div ref={wrapRef} className="relative min-h-0 min-w-0 flex-1">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 block h-full w-full"
          style={{ touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
          onPointerLeave={onPointerLeave}
          aria-label={bufName}
        />
        {!here && !paneReading.length ? (
          <div className="pointer-events-none absolute inset-0 grid place-items-center px-[24px] text-center text-[12px] leading-[1.5] text-[color:var(--muted)]">
            {model.error ? "The range's traces could not be read."
              : buf === FLEET ? (Object.values(store.boxes).some((at) => at.buf && at.buf !== FLEET) ? "Every machine is in another buffer." : "No traces in this range.")
                : buf === FINDINGS ? "The review kept no findings in this range."
                  : buf === TUCKED ? "Nothing tucked away. Drag a card or a trace onto Tucked away below, or pick one and press Delete."
                    : "Nothing here yet. Drag a machine, a trace or a card from another pane into this one."}
          </div>
        ) : null}
        {tip ? (
          <div ref={tipRef} className="pointer-events-none absolute z-[5] rounded-[10px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[10px] py-[8px] shadow-[var(--node-shadow-lift)]">
            <TipBody tip={tip} scene={scene} model={model} group={group} />
          </div>
        ) : null}
      </div>
      )}
      <div className="@container flex h-[32px] min-w-0 flex-none items-stretch border-t border-[color:var(--line-soft)] bg-[color:var(--bg)] text-[11px]" data-canvas-controls="">
        {compact ? (
          <BufferMenu buffers={store.buffers} buf={wsId ? null : buf} counts={counts} onPick={(id) => api.setPane(pane.id, { buf: id })} onAdd={() => api.addBuffer(pane.id)} />
        ) : (
        <div className="flex min-w-0 flex-shrink items-stretch" data-fleet-buffers="">
          {store.buffers.map((b, i) => (
            <BufferCell
              key={b.id}
              b={b}
              first={i === 0}
              on={buf === b.id}
              count={b.id === FLEET || b.id === FINDINGS ? 0 : counts.get(b.id) || 0}
              onPick={() => api.setPane(pane.id, { buf: b.id })}
              onRename={(name) => api.renameBuffer(b.id, name)}
              onRemove={() => api.removeBuffer(b.id)}
            />
          ))}
          <HoverTooltip placement="top" label="A buffer of your own, shown in this pane: drag anything into it to keep it there, at its own scale">
            <button type="button" className={iconCell} onClick={() => api.addBuffer(pane.id)} aria-label="New buffer">+</button>
          </HoverTooltip>
          <BufferCell b={{ id: TUCKED, name: "Tucked away" }} first={false} on={buf === TUCKED} count={counts.get(TUCKED) || 0} onPick={() => api.setPane(pane.id, { buf: TUCKED })} onRename={() => {}} onRemove={() => {}} />
        </div>
        )}
        <WorkspaceMenu workspaces={workspaces} buf={buf} onPick={(id) => api.setPane(pane.id, { buf: id })} />
        {buf === FLEET ? (
          <div className="flex flex-none items-stretch" role="group" aria-label="Show the fleet by" data-fleet-grouping="">
            <span className="flex items-center border-l border-[color:var(--line-soft)] px-[9px] text-[color:var(--muted-soft)] @max-[900px]:hidden">Group</span>
            {GROUPINGS.map(([id, name]) => (
              <HoverTooltip
                key={id}
                placement="top"
                label={id === "focus" ? "What a person should look at first, large in the middle: the traces a standard was met in; around them, the machines they ran on and the rest of Triage"
                  : id === "concern" ? "Every machine in one flow, what most needs a look first"
                  : id === "work" ? "Machines whose traces fall in the same topics, framed together and named for the topics that set them apart"
                    : "A frame per department in the directory, a person's machines side by side"}
              >
                <button type="button" className={`${cell} ${group === id ? cellOn : ""}`} aria-pressed={group === id} onClick={() => { autoView.current = true; api.setPane(pane.id, { group: id }); }}>{name}</button>
              </HoverTooltip>
            ))}
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 items-center gap-[8px] border-l border-[color:var(--line-soft)] px-[9px] text-[color:var(--muted)] @max-[640px]:hidden" role="status">
          {paneReading.length ? <Working /> : null}
          <span className="min-w-0 truncate leading-[16px]">{status}</span>
        </div>
        <div className="ml-auto flex flex-none items-stretch">
          {/* A workspace's board has its own controls, under it. */}
          {wsId ? null : (
          <>
          <HoverTooltip placement="top" label="Put this buffer in order: every card and frame back in its place, at the size it has">
            <button type="button" className={`${cell} @max-[560px]:hidden`} onClick={() => { needsFit.current = true; api.arrange(pane.id, aspect); }}>Arrange</button>
          </HoverTooltip>
          <button type="button" className={`${iconCell} @max-[480px]:hidden`} onClick={() => { autoView.current = false; const S = sizeRef.current; const v = viewRef.current; const k = clamp(v.k / 1.4, 0.03, 3); viewRef.current = { k, ox: S.w / 2 - ((S.w / 2 - v.ox) / v.k) * k, oy: S.h / 2 - ((S.h / 2 - v.oy) / v.k) * k }; lastZoom.current = performance.now(); viewChanged(); invalidate(); }} aria-label="Zoom out">&minus;</button>
          <button type="button" className={`${iconCell} @max-[480px]:hidden`} onClick={() => { autoView.current = false; const S = sizeRef.current; const v = viewRef.current; const k = clamp(v.k * 1.4, 0.03, 3); viewRef.current = { k, ox: S.w / 2 - ((S.w / 2 - v.ox) / v.k) * k, oy: S.h / 2 - ((S.h / 2 - v.oy) / v.k) * k }; lastZoom.current = performance.now(); viewChanged(); invalidate(); }} aria-label="Zoom in">+</button>
          <HoverTooltip
            wide
            placement="top"
            label={(
              <div className="grid gap-[3px] text-[11.5px] leading-[1.4]">
                <div>Fleet, Look now: the traces a standard was met in, large in the middle, the machines they ran on beside them and the rest of Triage under them. By Machines: a card per machine, its range day by day and the traces that most need a look; Work frames machines whose traces fall in the same topics, Team the directory&apos;s departments.</div>
                <div>Findings: the review&apos;s flow as nodes, from what the reviews wrote to where Triage left each finding, each outcome a card of the traces behind it.</div>
                <div>Split puts another pane beside this one. Drag anything from one pane into another, onto a buffer&apos;s name below, or onto an investigation in the list; it lands the size it was on screen. Pointing at a trace or a machine lights it in every pane.</div>
                <div>Drag a card by its head or edge to move it; size it by its corner, or with Option and scroll; widen it by its right edge. Shift and drag picks several; Delete tucks what is picked away; Cmd or Ctrl and Z undoes. Scroll to move around, Cmd or Ctrl and scroll to zoom; double-click a card, or its Zoom in, to fill the pane with it; F to see everything.</div>
              </div>
            )}
          >
            <button type="button" className={iconCell} aria-label="How these panes work">?</button>
          </HoverTooltip>
          </>
          )}
          {canSplit ? (
            <HoverTooltip placement="top" label="Another pane beside this one">
              <button type="button" className={cell} onClick={() => api.split(pane.id)} data-fleet-split="">Split</button>
            </HoverTooltip>
          ) : null}
          {count > 1 ? (
            <HoverTooltip placement="top" label="Close this pane; what it shows stays where it is">
              <button type="button" className={iconCell} onClick={() => api.close(pane.id)} aria-label="Close this pane">&times;</button>
            </HoverTooltip>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// A card's foot clicked: all of it, or back to what needs a look.
function placeOpen(s, o, buf) {
  const at = s.boxes[o.id] || {};
  const where = o.placed ? { ...(o.kind === "machine" ? { buf } : {}), x: o.x, y: o.y } : {};
  return { ...s, boxes: { ...s.boxes, [o.id]: { ...at, ...where, open: !o.open } } };
}

// The seam between two panes: the knob shows on hover, and a drag shares the
// two panes' room between them.
function PaneDivider({ onStart, onMove, onEnd }) {
  const [active, setActive] = useState(false);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the panes"
      className="group relative flex w-[10px] flex-none cursor-col-resize items-center justify-center"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture is an enhancement */ }
        setActive(true);
        onStart(e.clientX);
      }}
      onPointerMove={(e) => { if (active) onMove(e.clientX); }}
      onPointerUp={() => { if (active) { setActive(false); onEnd(); } }}
      onPointerCancel={() => { if (active) { setActive(false); onEnd(); } }}
    >
      <span className={`h-[48px] w-[3px] rounded-full bg-[color:var(--line-strong)] transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
    </div>
  );
}

// The hand, over the whole page.
function DeckOverlay({ deck, theme }) {
  const ref = useRef(null);
  const [host, setHost] = useState(null);
  useEffect(() => { setHost(document.body); }, []);
  useEffect(() => {
    deck.overlay = { draw: () => paintOverlay(ref.current, deck, theme) };
    deck.schedule();
    return () => { deck.overlay = null; };
  }, [deck, theme, host]);
  if (!host) return null;
  return createPortal(<canvas ref={ref} aria-hidden="true" className="pointer-events-none fixed left-0 top-0 z-[70]" style={{ width: 0, height: 0 }} data-fleet-hand="" />, host);
}

export default function FleetCanvas({ range = "1M", onRangeChange = null, onFind = null }) {
  const model = useFleetModel(range);
  const modelRef = useRef(model);
  modelRef.current = model;
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const deckRef = useRef(null);
  const [opened] = useState(readStore);
  const [store, setStore] = useState(opened.store);
  const storeRef = useRef(store);
  storeRef.current = store;
  const deck = useMemo(makeDeck, []);
  const hist = useRef({ past: [], future: [], base: null });
  const themeVersion = useThemeVersion();
  const [deckW, setDeckW] = useState(0);
  const deckWRef = useRef(0);
  deckWRef.current = deckW;
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  useLayoutEffect(() => {
    const el = deckRef.current;
    if (!el) return undefined;
    const measure = () => setDeckW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const theme = useMemo(() => {
    const host = deckRef.current;
    const base = readTheme(host);
    const v = (name, fallback) => cssVar(name, fallback, host);
    const muted = v("--muted", "#8a8278");
    return {
      ...base, danger: v("--danger", "#d4503e"), warn: v("--warn", "#c08a2d"), panel: v("--panel", base.bg),
      diagramDot: v("--diagram-dot", "rgba(232,224,215,0.11)"), nodeShadow: v("--node-shadow-ink", "rgba(0,0,0,0.12)"),
      nodeShadowLift: v("--node-shadow-lift-ink", "rgba(0,0,0,0.3)"),
      sev: {
        critical: v("--severity-critical", "#78190d"), high: v("--severity-high", "#b4481f"), medium: v("--severity-medium", "#d4954a"),
        low: v("--severity-low", "#b3a894"), none: muted, unrated: muted, unknown: muted, other: muted,
      },
    };
  }, [themeVersion, mounted]); // eslint-disable-line react-hooks/exhaustive-deps

  // What the panes' groupings and the Findings need, read when first shown.
  const needsWork = store.panes.some((p) => p.buf === FLEET && p.group === "work");
  const needsPeople = store.panes.some((p) => p.buf === FLEET && p.group === "team");
  const needsSums = store.panes.some((p) => p.buf === FINDINGS);
  const work = useRead(needsWork ? `work|${rangeKey}` : null, () => readWork(range));
  const people = useRead(needsPeople ? `people|${rangeKey}` : null, () => readPeople(range));
  const sums = useRead(needsSums ? `flow|${rangeKey}` : null, () => readFlowSums(range));
  const personIds = useMemo(() => [...new Set([...(people.data?.people?.values() || [])].flat().map((p) => p.id))], [people.data]);
  const personTick = useLabels(useMemo(() => personIds.map((id) => ({ dim: "identities", id })), [personIds]));
  const groupsWork = useMemo(() => (work.status === "done" ? groupByWork(model.machines, work.data) : null), [work.status, work.data, model.machines]);
  const groupsTeam = useMemo(() => (people.status === "done" ? groupByTeam(model.machines, people.data.people, model.rank) : null), [people.status, people.data, model.machines, model.rank, personTick]); // eslint-disable-line react-hooks/exhaustive-deps
  // The flow, from what the reviews wrote to where Triage left each finding.
  const flow = useMemo(() => {
    if (!needsSums || !model.findingsReady || sums.status === "loading") return null;
    const verdicts = {};
    for (const f of model.findings) {
      if (f.verdict) verdicts[f.id] = { verdict: f.verdict, escalates: !!f.met?.length, standardAsked: !!f.asked?.length, metBy: f.met || [] };
    }
    return buildReviewFlow({
      judged: sums.status === "done" ? sums.data : null,
      findings: model.findings.map((f) => ({ id: f.id, severity: f.severity, session: f.session })),
      clipped: model.clipped,
      verdicts,
    });
  }, [needsSums, model.findingsReady, model.findings, model.clipped, sums.status, sums.data]);
  const outcomes = useMemo(() => (model.findingsReady ? outcomesOf(model) : NONE), [model]);
  // The workspaces kept in this browser, the most recently used first: any
  // pane can show one.
  const workspaces = useTraceCanvases();
  const reading = { work: work.status === "loading", people: people.status === "loading", sums: sums.status === "loading" };

  // What the panes change: the store, written down when a gesture ends, and
  // what was moved remembered for undo.
  const api = useMemo(() => {
    const put = (next, keep = true) => {
      storeRef.current = next;
      setStore(next);
      if (keep) writeStore(next);
    };
    // Each step back holds the deck as it was and, when the step laid
    // sessions on a workspace or took them off, how to undo and redo that.
    const remember = (prev, ws = null) => {
      const h = hist.current;
      h.past.push({ s: prev, ws });
      if (h.past.length > 80) h.past.shift();
      h.future = [];
    };
    const commit = (next, ws = null) => {
      remember(storeRef.current, ws);
      put(next, true);
    };
    const gestureBegin = () => { if (!hist.current.base) hist.current.base = storeRef.current; };
    const gestureEnd = (next, ws = null) => {
      const base = hist.current.base;
      hist.current.base = null;
      if (next) put(next, false);
      if (base && (base !== storeRef.current || ws)) remember(base, ws);
      writeStore(storeRef.current);
    };
    const gestureRevert = () => {
      const base = hist.current.base;
      hist.current.base = null;
      if (base) put(normalStore({ ...base, panes: storeRef.current.panes }), true);
    };
    const restore = (s) => put(normalStore({ ...s, panes: storeRef.current.panes }), true);
    const undo = () => {
      const h = hist.current;
      const prev = h.past.pop();
      if (!prev) return;
      h.future.push({ s: storeRef.current, ws: prev.ws });
      restore(prev.s);
      prev.ws?.undo();
    };
    const redo = () => {
      const h = hist.current;
      const next = h.future.pop();
      if (!next) return;
      h.past.push({ s: storeRef.current, ws: next.ws });
      restore(next.s);
      next.ws?.redo();
    };
    // Sessions laid on workspaces and taken off them: [workspace, on, off]
    // for each, applied now; what undoes and redoes it, or null when it
    // changed nothing. A workspace keeps its newest sessions, so laying on
    // can push its oldest off; those are counted in `pushed`.
    const layers = (r) => ({ layered: r.layered, readings: r.readings, sizes: r.sizes, order: r.order, dismissed: r.dismissed });
    const wsChange = (changes) => {
      const ids = (sid) => sid.toLowerCase();
      const before = new Map();
      for (const [id, on, off] of changes) {
        if (!id || (!on.length && !off.length)) continue;
        if (!before.has(id)) before.set(id, layers(traceCanvasSnapshot(id)));
        const rec = traceCanvasSnapshot(id);
        const fresh = [...new Set(on.map(ids))].filter((sid) => sid !== rec.anchor && !rec.layered.some((t) => t.id === sid));
        if (fresh.length) layOnTraceCanvas(id, fresh.map((sid) => ({ id: sid, title: modelRef.current.sessions.get(sid)?.title || "" })));
        for (const sid of new Set(off.map(ids))) takeOffTraceCanvas(id, sid);
      }
      const after = new Map([...before.keys()].map((id) => [id, layers(traceCanvasSnapshot(id))]));
      const diffs = [...before].map(([id, was]) => {
        const had = was.layered.map((t) => t.id);
        const now = after.get(id).layered.map((t) => t.id);
        return { id, added: now.filter((x) => !had.includes(x)), removed: had.filter((x) => !now.includes(x)) };
      }).filter((d) => d.added.length || d.removed.length);
      if (!diffs.length) return null;
      // Undone and redone exactly: each workspace as it was before, or after.
      return {
        diffs,
        undo: () => { for (const [id, was] of before) restoreTraceCanvasLayers(id, was); },
        redo: () => { for (const [id, now] of after) restoreTraceCanvasLayers(id, now); },
      };
    };
    const bufferName = (id) => (id === TUCKED ? "Tucked away" : isWs(id) ? `"${short(wsTitle(wsOf(id)), 44)}"` : storeRef.current.buffers.find((b) => b.id === id)?.name || "the buffer");
    const nameOf = (it) => (it.kind === "session" ? `"${short(titleOf(modelRef.current.sessions.get(it.sid) || { id: it.sid }), 44)}"` : it.kind === "outcome" ? OUTCOMES.find((o) => `o:${o.id}` === it.id)?.label || "The card" : machineLabel(it.id).name);
    // Where things sent to a buffer land: near the top left of the view a
    // pane shows it at (or last showed it at), a step down and across from
    // the last thing sent there.
    const landing = (buf) => {
      const s = storeRef.current;
      let v = null;
      for (const [, pane] of deck.panes) if (pane.buf() === buf) v = pane.view();
      v = v || s.panes.map((p) => p.views?.[buf]).find(Boolean) || { k: 0.8, ox: 40, oy: 40 };
      const n = (Object.values(s.boxes).filter((a) => a.buf === buf).length + Object.values(s.cards).filter((a) => a.buf === buf).length) % 8;
      return { x: (60 + 28 * n - v.ox) / v.k, y: (60 + 28 * n - v.oy) / v.k, k: v.k };
    };
    // Sessions laid on a workspace from what was dropped on its name: each
    // session, a machine's or an outcome's that most need a look; each goes
    // where its time puts it on the workspace's clock.
    // Sessions carried off another workspace's pane come off that one.
    const toWorkspace = (id, items) => {
      const sids = [...new Set(items.flatMap((it) => (it.kind === "session" ? [it.sid] : sessionsOf(it.o || {}))))];
      const s = storeRef.current;
      const ws = wsChange([[id, sids, []]]);
      commit(s, ws);
      return wsSaid(id, ws, sids.length);
    };
    const wsSaid = (id, ws, asked) => {
      const d = ws?.diffs.find((x) => x.id === id);
      const added = d?.added.length || 0;
      const pushed = d?.removed.length || 0;
      const title = `"${short(wsTitle(id), 44)}"`;
      if (!added) return asked ? `Already on ${title}` : "";
      return `Laid ${plural(added, "trace")} on ${title}${pushed ? `; it keeps its newest ${MAX_ON_WORKSPACE}, so ${plural(pushed, "older one")} came off` : ""}`;
    };
    const send = (items, buf) => {
      if (isWs(buf)) return toWorkspace(wsOf(buf), items);
      let s = storeRef.current;
      const spot = landing(buf);
      items.forEach((it, i) => {
        const off = (28 * i) / spot.k;
        s = placeItem(s, it, buf, spot.x + off, spot.y + off, buf === TUCKED ? it.s || 1 : (it.zs || 1) / spot.k);
      });
      commit(s);
      return items.length === 1 ? `${nameOf(items[0])} is in ${bufferName(buf)} now` : `${items.length} things are in ${bufferName(buf)} now`;
    };
    // Back where they came from: a machine to the fleet's flow, an outcome to
    // the Findings, a session into its card.
    const back = (s, it) => {
      if (it.kind === "session") {
        const { [it.sid]: _gone, ...cards } = s.cards;
        return { ...s, cards };
      }
      const { x: _x, y: _y, buf: _b, ...rest } = s.boxes[it.id] || {};
      return { ...s, boxes: { ...s.boxes, [it.id]: rest } };
    };
    const untuck = (items) => {
      commit(items.reduce(back, storeRef.current));
      return items.length === 1 ? `${nameOf(items[0])} is back` : `${items.length} things are back`;
    };
    const sessionsOf = (o) => {
      if (o.sid) return [o.sid];
      if (o.kind === "machine") return (o.m.flagged.length ? o.m.flagged.slice(0, 12) : o.m.clean.slice(0, 6)).map((s) => s.id);
      if (o.kind === "outcome") return o.o.groups.slice(0, 12).map((g) => g.s.id);
      return [];
    };
    const layOn = (wsId, ids) => {
      const m = modelRef.current;
      const entries = [...new Set(ids)].slice(0, 24).map((id) => ({ id, title: m.sessions.get(id)?.title || "" }));
      if (!entries.length) return "";
      layOnTraceCanvas(wsId, entries);
      const title = traceCanvasSnapshot(wsId)?.title || "the investigation";
      return `Laid ${entries.length === 1 ? `"${short(titleOf(m.sessions.get(entries[0].id) || { id: entries[0].id }), 44)}"` : plural(entries.length, "trace")} on "${short(title, 44)}"`;
    };
    // A workspace in a pane of the deck: the one showing it already, else a
    // new pane beside `fromPane` (or after the last), else the last pane
    // other than `fromPane`, given over to it.
    const showWorkspace = (id, fromPane = null) => {
      const buf = `${WS}${id}`;
      const s = storeRef.current;
      showTraceCanvasInDeck(id);
      const shown = s.panes.find((p) => p.buf === buf);
      if (shown) {
        deck.active = shown.id;
        const el = deckRef.current?.querySelector(`[data-fleet-pane="${shown.id}"]`);
        if (el) {
          el.style.boxShadow = "inset 0 0 0 1px var(--green)";
          setTimeout(() => el.style.removeProperty("box-shadow"), 700);
        }
        return shown.id;
      }
      if (s.panes.length < MAX_PANES) {
        const at = s.panes.findIndex((p) => p.id === fromPane);
        const i = at < 0 ? s.panes.length - 1 : at;
        const cur = s.panes[i];
        const next = { ...freshPane(buf), w: cur.w / 2 };
        let panes = [...s.panes.slice(0, i), { ...cur, w: cur.w / 2 }, next, ...s.panes.slice(i + 1)];
        const sum = panes.reduce((n, p) => n + p.w, 0);
        const room = deckWRef.current - 10 * (panes.length - 1);
        if (panes.some((p) => (p.w / sum) * room < PANE_MIN)) panes = panes.map((p) => ({ ...p, w: 1 }));
        put({ ...s, panes });
        deck.active = next.id;
        return next.id;
      }
      const given = [...s.panes].reverse().find((p) => p.id !== fromPane) || s.panes[s.panes.length - 1];
      put({ ...s, panes: s.panes.map((p) => (p.id === given.id ? { ...p, buf } : p)) });
      deck.active = given.id;
      return given.id;
    };

    const setPane = (id, patch) => {
      const s = storeRef.current;
      put({ ...s, panes: s.panes.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
    };
    const setView = (id, buf, view) => {
      const s = storeRef.current;
      if (!view || !s.panes.some((p) => p.id === id)) return;
      put({ ...s, panes: s.panes.map((p) => (p.id === id ? { ...p, views: { ...p.views, [buf]: view } } : p)) });
    };
    const split = (id) => {
      const s = storeRef.current;
      const i = s.panes.findIndex((p) => p.id === id);
      if (i < 0 || s.panes.length >= MAX_PANES) return;
      const cur = s.panes[i];
      const half = cur.w / 2;
      const next = { ...freshPane(cur.buf === FLEET ? FINDINGS : FLEET), w: half };
      let panes = [...s.panes.slice(0, i), { ...cur, w: half }, next, ...s.panes.slice(i + 1)];
      // A pane split narrower than a pane may be: the room shared out evenly.
      const sum = panes.reduce((n, p) => n + p.w, 0);
      const room = deckWRef.current - 10 * (panes.length - 1);
      if (panes.some((p) => (p.w / sum) * room < PANE_MIN)) panes = panes.map((p) => ({ ...p, w: 1 }));
      put({ ...s, panes });
    };
    const close = (id) => {
      const s = storeRef.current;
      const i = s.panes.findIndex((p) => p.id === id);
      if (i < 0 || s.panes.length < 2) return;
      const gone = s.panes[i];
      const panes = s.panes.filter((p) => p.id !== id);
      const j = Math.max(0, i - 1);
      panes[j] = { ...panes[j], w: panes[j].w + gone.w };
      put({ ...s, panes });
    };
    const addBuffer = (paneId) => {
      const s = storeRef.current;
      const n = s.buffers.filter((b) => b.id !== FLEET && b.id !== FINDINGS).length + 1;
      const id = newId("b");
      put({ ...s, buffers: [...s.buffers, { id, name: `Buffer ${n}` }], panes: s.panes.map((p) => (p.id === paneId ? { ...p, buf: id, views: { ...p.views, [id]: { k: 0.8, ox: 40, oy: 40 } } } : p)) });
    };
    const renameBuffer = (id, name) => {
      const s = storeRef.current;
      put({ ...s, buffers: s.buffers.map((b) => (b.id === id ? { ...b, name } : b)) });
    };
    const removeBuffer = (id) => {
      let s = storeRef.current;
      // What was in it goes back where it came from.
      for (const [key, at] of Object.entries(s.boxes)) if (at.buf === id) s = back(s, { kind: key.startsWith("o:") ? "outcome" : "machine", id: key });
      for (const [sid, at] of Object.entries(s.cards)) if (at.buf === id) s = back(s, { kind: "session", sid });
      commit(normalStore({ ...s, buffers: s.buffers.filter((b) => b.id !== id), panes: s.panes.map((p) => (p.buf === id ? { ...p, buf: FLEET } : p)) }));
    };
    // Puts a pane's buffer in order: on the Fleet and the Findings every card
    // back in its place in the flow (and every frame, grouped), at the size
    // it has; any other buffer packed from the top left, each at its own
    // scale.
    const arrange = (paneId, aspect) => {
      const pane = deck.panes.get(paneId);
      const sc = pane?.scene();
      if (!sc) return;
      const s = storeRef.current;
      const boxes = { ...s.boxes };
      const cards = { ...s.cards };
      let frames = s.frames;
      if (sc.buf === FLEET || sc.buf === FINDINGS) {
        for (const b of sc.boxes) {
          const { x: _x, y: _y, ...rest } = boxes[b.id] || {};
          boxes[b.id] = rest;
        }
        for (const c of sc.cards) delete cards[c.sid];
        if (sc.buf === FLEET) {
          const mode = s.panes.find((p) => p.id === paneId)?.group || "concern";
          frames = { ...s.frames, [mode]: {} };
        }
      } else {
        const at = pack([
          ...sc.boxes.map((b) => ({ key: b.key, w: b.wu * b.s, h: b.hu * b.s })),
          ...sc.cards.map((c) => ({ key: c.key, w: c.wu * c.s, h: c.hu * c.s })),
        ], aspect);
        for (const b of sc.boxes) boxes[b.id] = { ...(boxes[b.id] || {}), buf: sc.buf, ...at.get(b.key) };
        for (const c of sc.cards) cards[c.sid] = { ...cards[c.sid], ...at.get(c.key) };
      }
      commit({ ...s, boxes, cards, frames, panes: s.panes.map((p) => (p.id === paneId ? { ...p, views: { ...p.views, [sc.buf]: null } } : p)) });
    };
    const touch = (key) => {
      const s = storeRef.current;
      if (s.z[s.z.length - 1] === key) return;
      put({ ...s, z: [...s.z.filter((x) => x !== key), key].slice(-400) }, false);
    };
    const patchBox = (id, patch, keep = true) => {
      const s = storeRef.current;
      put({ ...s, boxes: { ...s.boxes, [id]: { ...(s.boxes[id] || {}), ...patch } } }, keep);
    };
    const patchCard = (sid, patch, keep = true) => {
      const s = storeRef.current;
      put({ ...s, cards: { ...s.cards, [sid]: { ...(s.cards[sid] || {}), ...patch } } }, keep);
    };
    const patchFrame = (gid, patch, keep = true) => {
      const s = storeRef.current;
      const mode = s.panes.find((p) => p.id === deck.active)?.group || "concern";
      const kept = s.frames?.[mode] || {};
      put({ ...s, frames: { ...s.frames, [mode]: { ...kept, [gid]: { ...(kept[gid] || {}), ...patch } } } }, keep);
    };
    const counts = () => {
      const s = storeRef.current;
      const n = new Map();
      for (const at of Object.values(s.boxes)) if (at.buf) n.set(at.buf, (n.get(at.buf) || 0) + 1);
      for (const at of Object.values(s.cards)) n.set(at.buf || FLEET, (n.get(at.buf || FLEET) || 0) + 1);
      return n;
    };
    return {
      store: () => storeRef.current, put, commit, gestureBegin, gestureEnd, gestureRevert, undo, redo,
      bufferName, nameOf, landing, send, untuck, sessionsOf, layOn, showWorkspace, wsChange, wsSaid,
      setPane, setView, split, close, addBuffer, renameBuffer, removeBuffer, arrange, touch, patchBox, patchCard, patchFrame, counts,
    };
  }, [deck]);

  // On a wide page, a deck seen for the first time opens with the Fleet and
  // the Findings side by side.
  const opening = useRef(opened.fresh);
  useEffect(() => {
    if (!opening.current || !deckW) return;
    opening.current = false;
    const s = storeRef.current;
    if (deckW >= 1250 && s.panes.length === 1) {
      const fleet = { ...s.panes[0], w: 1.55 };
      api.put({ ...s, panes: [fleet, { ...freshPane(FINDINGS), w: 1 }] });
    }
  }, [deckW, api]);

  // The keys: undo and redo for every pane, the rest for the pane last used.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.closest?.("input, textarea, select, [contenteditable='true'], .cm-editor")) return;
      if (!deckRef.current?.isConnected) return;
      if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z")) {
        if (!deck.active || deck.panes.get(deck.active)?.board?.()) return;
        e.preventDefault();
        if (e.shiftKey) api.redo();
        else api.undo();
        deck.invalidate();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      deck.panes.get(deck.active)?.key(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deck, api]);
  useEffect(() => () => clearLit(deck), [deck]);
  useEffect(() => {
    const heard = () => deck.invalidate();
    lanesHeard.add(heard);
    return () => { lanesHeard.delete(heard); };
  }, [deck]);
  useEffect(() => onDeckWorkspace((id, extra) => {
    if (extra?.trace || extra?.focus) handedBoards.set(id, extra);
    api.showWorkspace(id, deck.active);
  }), [api, deck]);

  // Sharing the room between two panes.
  const sizing = useRef(null);
  const startResize = (i, clientX) => {
    const slots = [...(deckRef.current?.querySelectorAll("[data-fleet-pane-slot]") || [])];
    const a = slots[i - 1]?.getBoundingClientRect();
    const b = slots[i]?.getBoundingClientRect();
    if (!a || !b) return;
    const panes = storeRef.current.panes;
    sizing.current = { i, x: clientX, aw: a.width, bw: b.width, wsum: panes[i - 1].w + panes[i].w };
  };
  const moveResize = (clientX) => {
    const r = sizing.current;
    if (!r) return;
    const total = r.aw + r.bw;
    const aw = clamp(r.aw + clientX - r.x, PANE_MIN, total - PANE_MIN);
    const s = storeRef.current;
    const panes = s.panes.map((p, j) => (j === r.i - 1 ? { ...p, w: (r.wsum * aw) / total } : j === r.i ? { ...p, w: (r.wsum * (total - aw)) / total } : p));
    api.put({ ...s, panes }, false);
  };
  const endResize = () => {
    sizing.current = null;
    writeStore(storeRef.current);
  };
  const canSplit = store.panes.length < MAX_PANES && deckW / (store.panes.length + 1) >= PANE_MIN;

  return (
    <div ref={deckRef} className="flex h-full min-h-0 min-w-0" data-fleet-canvas="">
      {store.panes.map((p, i) => (
        <Fragment key={p.id}>
          {i ? <PaneDivider onStart={(x) => startResize(i, x)} onMove={moveResize} onEnd={endResize} /> : null}
          <div className="flex min-h-0 min-w-0 flex-col" style={{ flex: `${p.w} 1 0px` }} data-fleet-pane-slot={p.id}>
            <Pane
              pane={p}
              count={store.panes.length}
              canSplit={canSplit}
              deck={deck}
              api={api}
              model={model}
              store={store}
              theme={theme}
              themeKey={themeVersion}
              groupsWork={groupsWork}
              groupsTeam={groupsTeam}
              flow={flow}
              outcomes={outcomes}
              reading={reading}
              range={range}
              onRangeChange={onRangeChange}
              onFind={onFind}
              workspaces={workspaces}
            />
          </div>
        </Fragment>
      ))}
      <DeckOverlay deck={deck} theme={theme} />
    </div>
  );
}
